(() => {
if (customElements.get('cart-drawer')) return;
/* Wild Gainz cart drawer.

   Owns the shell: opening, closing, the scroll lock, the focus trap, the
   header cart icon, and the small API Dawn's scripts expect from a
   <cart-drawer> element (product-form.js finds this tag and calls
   getSectionsToRender / renderContents / setActiveElement after an add).

   It also renders the body: lines, quantities, tier aware prices, the free
   shipping bar, delivery frequency and the one offer card. Everything it
   needs comes from Theme settings (Wild Gainz cart drawer), so the same
   cart, with the same offers, opens on every page. The buy box hands over
   its nicer flavor names when it happens to be on the page.
*/

const CONFIG = (() => {
  const el = document.querySelector('[data-wg-cart-config]');
  const fallback = { freeShipQty: 0, moneyFormat: '${{amount}}', txt: {} };
  if (!el) return fallback;
  try {
    return Object.assign(fallback, JSON.parse(el.textContent));
  } catch (error) {
    return fallback;
  }
})();

const esc = (value) =>
  String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/* Shopify money formats carry their own separators; formatting by hand is
   the only way to stay right in every locale the shop sells in. */
function money(cents) {
  const amount = (Math.round(cents) / 100).toFixed(2);
  const [whole, fraction] = amount.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const format = CONFIG.moneyFormat || '${{amount}}';
  return format.replace(/\{\{\s*\w+\s*\}\}/, grouped + '.' + fraction);
}

/* Two carts, on purpose.

   serverCart is the last cart Shopify gave us, untouched. Line keys are read
   from it and nowhere else, because a key is a server fact: Shopify rotates
   every key on every change, and the drawer's own optimistic paints never
   move a key. cart is what the panel draws: a copy of serverCart with
   everything the shopper has pressed and the server has not yet confirmed
   laid over it (see rebase). Painting from a plain server response was how
   a press on one line snapped another line's fresh quantity back to its old
   one until its own write landed. */
let cart = null;
let serverCart = null;
/* Bumped on every server cart accepted, so a /cart.js read that was issued
   before a write landed can be recognised as older than what we hold. */
let cartVersion = 0;
let fetchSeq = 0;
/* Signature of the cart the panel currently shows; see render(). */
let paintedSignature = null;

async function fetchCart() {
  const seq = ++fetchSeq;
  const version = cartVersion;
  try {
    const response = await fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      /* A back-navigation can otherwise be handed this straight out of the
         HTTP cache, which is the one moment it is guaranteed to be wrong. */
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('cart');
    const data = await response.json();
    /* Issued before a write that has since landed, or before a later read:
       an older picture of the cart, which must not paint over a newer one.
       Nothing is lost by dropping it; whatever it knew, the newer one knows. */
    if (seq !== fetchSeq || version !== cartVersion) return cart;
    acceptCart(data);
  } catch (error) {
    /* A blip on a tab switch used to throw the cart away and put "your cart
       is updated, view cart" over a perfectly good line list. The last cart
       we had is far more likely to be right than nothing. Only a drawer that
       never had one shows the fallback. */
  }
  return cart;
}

/* The server has spoken: this is the cart now. */
function acceptCart(data) {
  cartVersion += 1;
  serverCart = data;
  /* A row the buy box said was coming has arrived: it is a line now. */
  expectedLines = expectedLines.filter((expected) => !findLineIn(data.items || [], expected.identity));
  /* An empty subscription resets the choices made about one. A cart that
     went through checkout in this tab, or was emptied on the cart page, must
     not hand its cadence choice to the next cart built here. */
  if (!subLines(data).length) {
    pinInterval(false);
    setHousehold(1);
  }
  rebase();
}

/* Rebuild the drawn cart from the server's, plus what is still owed to it. */
function rebase() {
  cart = serverCart ? JSON.parse(JSON.stringify(serverCart)) : null;
  if (cart) overlayPending(cart);
}

/* One write at a time, in the order they were asked for. Every request here
   rotates the keys of every line, so two in flight at once is one of them
   sent with a key the other just invalidated, which Shopify answers with a
   400 and the drawer used to answer with a rollback. The body is built
   inside the lock, so a key is read at the last possible moment, off the
   cart the server holds right now. */
let writeChain = Promise.resolve();
let writesInFlight = 0;
function withWriteLock(fn) {
  writesInFlight += 1;
  const run = writeChain.then(fn, fn);
  writeChain = run.then(() => {}, () => {});
  run.then(() => { writesInFlight -= 1; }, () => { writesInFlight -= 1; });
  return run;
}

/* A cart write. build() runs inside the lock and returns the request body,
   or null to send nothing (the line it meant is gone, which is the normal
   end of a remove). /cart/change.js answers with the whole cart, which is
   accepted as the truth; /cart/add.js answers with the lines it added, so
   the cart is re-read before the lock is released and nothing else can get
   between the add and the read. */
function cartWrite(url, build) {
  return withWriteLock(async () => {
    const body = build();
    if (!body) return null;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      cache: 'no-store',
      body: JSON.stringify(Object.assign({}, body, { sections: 'cart-icon-bubble' })),
    });
    if (!response.ok) {
      const refused = new Error('write');
      refused.status = response.status;
      throw refused;
    }
    const data = await response.json();
    updateBubble(data.sections);
    if (url.indexOf('/cart/add.js') === 0) {
      await fetchCart();
    } else {
      acceptCart(data);
    }
    return data;
  });
}

function updateBubble(sections) {
  const html = sections && sections['cart-icon-bubble'];
  const bubble = document.getElementById('cart-icon-bubble');
  if (!html || !bubble) return;
  try {
    /* The section comes wrapped in its own .shopify-section; the element
       with the id is the header's link around it, which the section does
       not include. Asking for the id found nothing, and the header count
       never moved for anything done inside the drawer. */
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const parsed = doc.querySelector('#cart-icon-bubble') || doc.querySelector('.shopify-section') || doc.body;
    if (parsed) bubble.innerHTML = parsed.innerHTML;
  } catch (error) {
    /* the count is cosmetic; a parse failure must not break the drawer */
  }
}

/* Coming back from checkout is the case that breaks a cart drawer. The
   browser restores this page from its back/forward cache with the JavaScript
   heap frozen exactly as we left it, so `cart` still holds the pre-checkout
   snapshot and the header count is whatever the server rendered on the way
   out. None of the work a fresh page load does happens again.

   That matters here because open() paints from `cart` before reconciling, to
   make the drawer feel instant. With a stale `cart` the shopper sees the old
   quantity appear and then correct itself, which reads as the cart losing an
   item. Refreshing the moment the page is restored means it is already right
   before anyone opens it. */
async function refreshFromServer() {
  await fetchCart();
  const drawer = document.querySelector('cart-drawer[data-wg-cart]');
  /* Repaint whether or not it is open, and before the header count is
     asked for: that request is cosmetic and must not hold the panel. */
  if (drawer) render(drawer);
  const version = cartVersion;
  try {
    const response = await fetch('/?sections=cart-icon-bubble', { cache: 'no-store' });
    /* Older than a write that landed while it was out: keep the count the
       write brought back. */
    if (response.ok && version === cartVersion) updateBubble(await response.json());
  } catch (error) {
    /* the header count is cosmetic */
  }
}

/* Holding the page still behind an open drawer.

   overflow:hidden on the body is what every theme does and what Safari
   ignores: the store keeps scrolling under the panel, and because a fixed
   element is measured against the large viewport, the panel appears to walk
   up from the bottom as the address bar hides. Pinning the body at minus its
   own scroll offset is the lock Safari honours, and putting the offset back
   on close is what stops the page jumping to the top afterwards. */
let lockedScrollY = 0;
let pageLocked = false;
let pinnedBody = false;

/* Pinning the body is a big hammer: it takes the page's scroll position to
   zero, which every position:sticky element and every scroll listener on the
   page reacts to, and on desktop it takes the scrollbar away and shifts the
   whole store sideways. Only Safari needs it. Everywhere else overflow
   hidden already holds the page, so everywhere else keeps what it had. */
const needsPinnedBody = (() => {
  try {
    const ua = navigator.userAgent || '';
    const iOS = /iP(hone|ad|od)/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
    const safari = /Safari/.test(ua) && !/Chrome|Chromium|Android|CriOS|FxiOS|EdgiOS/.test(ua);
    return iOS || safari;
  } catch (error) {
    return false;
  }
})();

/* Anything on the page that decides how to look from the scroll position,
   and does it from the scroll EVENT rather than by reading the offset. The
   sticky header is a delta machine: pinning the body reads to it as a jump
   to the top, and putting the offset back reads as a fast scroll down, so
   it hides itself and takes the cart icon with it, right after the shopper
   used that icon. Their own state ends up correct, because the restoring
   scroll event tells them where the shopper really is; it is only the
   classes those two phantom scrolls left behind that need putting back. */
const SCROLL_REACTIVE = ['.section-header', 'wildgainz-sticky-cta'];
let scrollReactiveClasses = [];

function rememberScrollReactive() {
  scrollReactiveClasses = [];
  SCROLL_REACTIVE.forEach((selector) => {
    document.querySelectorAll(selector).forEach((el) => {
      scrollReactiveClasses.push({ el: el, className: el.className });
    });
  });
}

function restoreScrollReactive() {
  const snapshot = scrollReactiveClasses;
  scrollReactiveClasses = [];
  if (!snapshot.length) return;
  /* after the restoring scroll has been delivered, so we are putting the
     classes back over the top of whatever it decided rather than under it */
  requestAnimationFrame(() => {
    snapshot.forEach((entry) => {
      if (entry.el.isConnected && entry.el.className !== entry.className) {
        entry.el.className = entry.className;
      }
    });
  });
}

function lockPage() {
  if (pageLocked) return;
  pageLocked = true;
  lockedScrollY = window.scrollY || document.documentElement.scrollTop || 0;
  const body = document.body;
  pinnedBody = needsPinnedBody;
  if (pinnedBody) {
    rememberScrollReactive();
    body.style.position = 'fixed';
    body.style.top = -lockedScrollY + 'px';
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
  }
  document.documentElement.style.overflow = 'hidden';
  body.style.overflow = 'hidden';
}

function unlockPage() {
  if (!pageLocked) return;
  pageLocked = false;
  const body = document.body;
  if (pinnedBody) {
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
  }
  body.style.overflow = '';
  document.documentElement.style.overflow = '';
  if (!pinnedBody) return;
  /* instant, so it is not a visible scroll back to where they already were */
  window.scrollTo(0, lockedScrollY);
  /* The header hides and reveals itself from a scroll listener, and pinning
     the body looked to it like the shopper had jumped to the top of the
     page. Tell it where they actually are, or the cart icon stays hidden
     after the panel closes. */
  try {
    window.dispatchEvent(new Event('scroll'));
  } catch (error) {
    /* nothing on the page insists on hearing it */
  }
  restoreScrollReactive();
}

window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  unlockCheckout(document.querySelector('cart-drawer[data-wg-cart]'));
  refreshFromServer();
});

/* Checkout finished in another tab, or the browser handed this one back
   without a bfcache restore. Throttled, because visibilitychange fires on
   every tab switch and the cart is not worth a request each time. */
let lastVisibilityRefresh = 0;
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState !== 'visible') return;
  const now = Date.now();
  if (now - lastVisibilityRefresh < 3000) return;
  lastVisibilityRefresh = now;
  refreshFromServer();
});

/* ---------- pricing, ported from the buy box so every page agrees ----------
   Configured mode mirrors the discount app's tiers by total subscription bag
   count; selling-plan mode trusts the allocation prices. With no products
   configured every sum below is zero and the drawer falls back to the cart's
   own total, which is the right answer for a shop that has not set it up. */

let FLAVORS = CONFIG.flavors || [];
let ourVariants = {};

/* "handle = name" lines from theme settings, so the drawer can say
   "Wild Strawberry" instead of the full product title. */
const LABELS = (() => {
  const map = {};
  String(CONFIG.labelMap || '')
    .split('\n')
    .forEach((line) => {
      const at = line.indexOf('=');
      if (at < 1) return;
      const handle = line.slice(0, at).trim().toLowerCase();
      const name = line.slice(at + 1).trim();
      if (handle && name) map[handle] = name;
    });
  return map;
})();

function indexFlavors() {
  ourVariants = {};
  FLAVORS.forEach((flavor) => {
    if (flavor.handle && LABELS[String(flavor.handle).toLowerCase()]) {
      flavor.label = LABELS[String(flavor.handle).toLowerCase()];
      flavor.emoji = '';
    }
    ourVariants[String(flavor.variantId)] = flavor;
  });
}
indexFlavors();

const months = () => CONFIG.defaultMonths || 2;

/* The intervals the shop actually sells, smallest first. */
function planMonths() {
  return Object.keys(CONFIG.planIds || {})
    .filter((key) => CONFIG.planIds[key])
    .map(Number)
    .filter((n) => n > 0)
    .sort((a, b) => a - b);
}

/* One bag per person per month is how the buy box sizes a supply, so a cart
   of N bags wants the N month plan for one person and the N/2 month plan for
   two. Anything past the longest plan on sale takes the longest. Falls back
   to the buy box default when nothing is set. */
function monthsForBags(bags, people) {
  const available = planMonths();
  if (!available.length) return months();
  /* people is given when the buy box asks about an order it has not sent
     yet, so the household it is about to store is used rather than the one
     from the last order. */
  const wanted = Math.max(1, bags / (people || household()));
  let best = available[0];
  available.forEach((n) => {
    if (Math.abs(n - wanted) < Math.abs(best - wanted)) best = n;
  });
  return best;
}

function planFor(flavor, m) {
  if (!flavor || !flavor.plans) return null;
  const explicit = (CONFIG.planIds || {})[String(m)];
  if (explicit) {
    const hit = flavor.plans.find((plan) => String(plan.planId) === String(explicit));
    if (hit) return hit;
  }
  const named = new RegExp('\\b' + m + '\\s*month', 'i');
  return flavor.plans.find((plan) => named.test(plan.planName)) || null;
}

function subPct(bags) {
  const key = Math.min(Math.max(bags, 1), 5);
  return (CONFIG.subPcts || {})[String(key)] || 0;
}

/* What one bag costs at the tier the cart has reached, rounded down to the
   cent.

   Per bag, not per line and not per cart, because the bag is the unit the
   shopper is shown and therefore the one they multiply. Two lines of two at
   25% off make the case: discount each line and the panel reads one figure
   twice above a cart total two cents adrift of them. Price the bag once and
   every figure derives from it, so the arithmetic anyone does in their head
   comes out.

   Down, because that is what the checkout bills, and the checkout is the
   only figure the shopper is actually charged. $44.99 at 25% is $33.7425,
   and two real checkouts on 20 Sep 2026 came to:

     3 bags   $134.97 -> $101.22    $33.74 a bag
     6 bags   $269.94 -> $202.44    $33.74 a bag

   The cart can disagree with that. While the discount app also discounts
   the cart, /cart.js returns $33.75 a bag, a cent more, and the drawer's
   total then ticks up a cent or two the moment each write lands: the
   optimistic paint is this table, the settled cart is the app's. Read off
   the live store on 21 Sep: $38.24 painting, $38.25 settling, $337.40
   painting, $337.50 settling.

   The answer to that is not to round differently here. The percentages are
   configured in this theme already, so the app has nothing to tell the cart
   that the cart does not know; it is only needed at checkout. With its cart
   side off, Shopify returns full price, cartStats finds no discount to
   trust and prices everything from this table, and every figure from the
   buy box through to the checkout is the same number.

   A cart that exists is otherwise priced by Shopify and read straight off
   cart.js; see cartStats. What is left for this is the optimistic frame
   between a press and the response, the offer's what-ifs, and the buy box,
   which quotes an order that has no cart yet. */
function discountedUnit(unitCents, bags) {
  if (CONFIG.priceSource !== 'configured') return unitCents;
  return Math.floor((unitCents * (100 - subPct(bags))) / 100);
}

function loopPct() {
  const flavor = FLAVORS[0];
  if (!flavor || !flavor.priceCents) return 0;
  const plan = planFor(flavor, months());
  return plan ? Math.max(0, Math.round((1 - plan.priceCents / flavor.priceCents) * 100)) : 0;
}

const offerPct = (bags) => (CONFIG.priceSource === 'configured' ? subPct(bags) : loopPct());

/* A subscription line's own price is the allocation's, already discounted;
   the price it is discounted from is the allocation's compare-at, when
   Shopify carries one. Without this the saving and the strike read zero
   the moment prices come from the selling plan instead of the tier table. */
function fullUnit(item, fallback) {
  const allocation = item.selling_plan_allocation;
  const compare = allocation && allocation.compare_at_price;
  return compare != null && compare > (allocation.price || 0) ? compare : fallback;
}

function cartStats(data) {
  let subBags = 0;
  let otpBags = 0;
  let subFull = 0;
  let otpFull = 0;
  let otpPay = 0;
  let ourCartPrice = 0;
  let otpSwitchIf = 0;
  /* The switch puts every bag on the cart's one cadence: the pinned choice,
     or the plan sized to all the bags once they are subscribed. Price the
     projection the same way. */
  let allOurBags = 0;
  ((data && data.items) || []).forEach((item) => {
    if (ourVariants[String(item.variant_id)]) allOurBags += item.quantity;
  });
  const switchMonths = intervalPinned() || monthsForBags(allOurBags);
  /* Collected rather than summed, because the tier is not known until every
     bag is counted and the price of a bag depends on it. */
  const subUnits = [];
  const allUnits = [];
  /* What each line is actually charged, keyed by identity, for the rows to
     print. Shopify's own figure where it has one, the tier table where it
     does not; see linePay below. */
  const paid = {};
  /* Shopify applies the subscription discount to the cart, not only at
     checkout: /cart.js on a six bag order returns total_price 20250 and
     total_discount 6744, the same $202.50 and $67.44 the checkout bills. So
     the cart is the authority on what a line costs and the tier table is only
     needed where the cart has no answer: an optimistic paint that has not
     reached the server yet, and the offer's what-if projections.

     A locally painted cart is marked, because its prices are ours rather than
     Shopify's and mixing the two would price the pressed line at the new tier
     while its neighbours sat at the old one. */
  const trustCart = !(data && data.__wgLocal);
  ((data && data.items) || []).forEach((item) => {
    const flavor = ourVariants[String(item.variant_id)];
    if (!flavor) return;
    /* Configured mode must base on the variant's full price: a cart line can
       already carry a selling-plan allocation discount, and stacking the tier
       on that would double discount the display. */
    const lineUnit = item.original_price != null ? item.original_price : item.price;
    const lineFull = item.original_line_price != null ? item.original_line_price : lineUnit * item.quantity;
    const lineFinal = item.final_line_price != null ? item.final_line_price : item.line_price;
    /* Only when Shopify has actually taken something off. An undiscounted
       line and a line the app has not reached look identical here, and both
       want the tier table. */
    const shopifyPay = trustCart && lineFinal != null && lineFinal < lineFull ? lineFinal : null;
    if (item.selling_plan_allocation) {
      /* A subscription line's own price already carries the allocation
         discount, so the full price has to come from the variant or the tier
         would stack on top of it. */
      const unit = CONFIG.priceSource === 'configured' ? flavor.priceCents : fullUnit(item, lineUnit);
      subBags += item.quantity;
      subFull += unit * item.quantity;
      subUnits.push({ unit: unit, qty: item.quantity, id: lineIdentity(item), pay: shopifyPay });
      allUnits.push({ unit: unit, qty: item.quantity });
    } else {
      /* No allocation on a one-time line: its own price is the full price,
         and it is the number printed next to it. */
      otpBags += item.quantity;
      otpFull += lineUnit * item.quantity;
      /* A one-time line is normally charged what it says, but an order level
         discount can reach it, and if Shopify says so then Shopify is right.
         No tier to wait for here, so it can be totalled on the spot. */
      const otpLinePay = shopifyPay != null ? shopifyPay : lineUnit * item.quantity;
      paid[lineIdentity(item)] = (paid[lineIdentity(item)] || 0) + otpLinePay;
      otpPay += otpLinePay;
      const plan = planFor(flavor, switchMonths);
      otpSwitchIf += (plan ? plan.priceCents : flavor.priceCents) * item.quantity;
      allUnits.push({
        unit: CONFIG.priceSource === 'configured' ? flavor.priceCents : lineUnit,
        qty: item.quantity,
      });
    }
    ourCartPrice += item.final_line_price != null ? item.final_line_price : item.line_price;
  });
  /* A projection: what these bags would come to at this tier. Used for the
     offer's what-ifs, where there is no cart to ask. */
  const priced = (lines, bags) =>
    lines.reduce((total, line) => total + discountedUnit(line.unit, bags) * line.qty, 0);
  const allBags = subBags + otpBags;
  /* And the real thing: Shopify's figure per line, falling back to the
     projection for that one line only. */
  let subPay = 0;
  subUnits.forEach((line) => {
    const pay = line.pay != null ? line.pay : discountedUnit(line.unit, subBags) * line.qty;
    paid[line.id] = (paid[line.id] || 0) + pay;
    subPay += pay;
  });

  return {
    subBags,
    otpBags,
    subFull,
    otpFull,
    ourCartPrice,
    otpSwitchIf,
    allBags,
    allFull: subFull + otpFull,
    /* what is actually charged: the subscription lines, the one-time lines,
       and the price of each line by identity for the rows to print */
    subPay,
    otpPay,
    linePay: paid,
    /* and the projection, for what one more bag or a switch would cost */
    allPay: priced(allUnits, allBags),
  };
}

/* One offer at a time. Choice overload kills conversion, so the drawer never
   stacks them. */
/* Successive upsells alternate their look. Accepting one swaps the card for
   the next offer in the same spot, and keeping the same colours reads as the
   click not registering rather than as a new offer arriving. */
let offerStreak = 0;

function computeOffer(data) {
  if (!FLAVORS.length) return null;
  const txt = CONFIG.txt || {};
  const stats = cartStats(data);
  if (CONFIG.offerSubOn && stats.otpBags > 0) {
    const payNow = stats.subPay + stats.otpFull;
    const payIf =
      CONFIG.priceSource === 'configured'
        ? stats.allPay
        : stats.subFull + stats.otpSwitchIf;
    const save = payNow - payIf;
    if (save > 0) {
      return {
        kind: 'sub',
        save: save,
        pct: CONFIG.priceSource === 'configured' ? subPct(stats.allBags) : loopPct(),
      };
    }
  }
  /* Add another bag: one honest +1, never a silent double. It suggests a
     flavor the cart does not already hold, so it doubles as the cross sell. */
  /* Stop once another bag wins nothing. Rather than a fixed cap, ask the tier
     table and the shipping threshold whether the next bag actually changes
     anything: a better rate, or crossing into free shipping. With the rate
     capped at 25% by three bags, that ends the ladder at three on its own, and
     keeps ending it in the right place if the table is ever re-cut.

     Past that point the drawer says nothing and lets them check out. The
     post-purchase one-click offers pick it up from there, where an extra ask
     costs nothing because the sale is already banked. */
  if (CONFIG.offerBagOn && stats.otpBags === 0 && stats.subBags > 0) {
    const nextBags = stats.subBags + 1;
    const betterRate = offerPct(nextBags) > offerPct(stats.subBags);
    const threshold = CONFIG.freeShipQty || 0;
    const winsShipping = threshold > 0 && stats.subBags < threshold && nextBags >= threshold;
    if (!betterRate && !winsShipping) return null;
    const next = stats.subBags + 1;
    const have = {};
    ((data && data.items) || []).forEach((item) => {
      if (ourVariants[String(item.variant_id)] && item.selling_plan_allocation) {
        have[String(item.variant_id)] = true;
      }
    });
    const missing = FLAVORS.find((flavor) => flavor.available && !have[String(flavor.variantId)]);
    return { kind: 'addbag', pct: offerPct(next), flavor: missing || null, winsShipping: winsShipping };
  }
  return null;
}

/* The cadence one line is on, in months. */
function lineMonths(line) {
  const plan = (line.selling_plan_allocation && line.selling_plan_allocation.selling_plan) || {};
  const ids = CONFIG.planIds || {};
  const match = Object.keys(ids).find((key) => ids[key] && String(plan.id) === String(ids[key]));
  if (match) return Number(match);
  const named = /(\d+)\s*month/i.exec(String(plan.name || ''));
  return named ? +named[1] : months();
}

/* Every one of our subscription lines. */
function subLines(data) {
  return ((data && data.items) || []).filter(
    (line) => ourVariants[String(line.variant_id)] && line.selling_plan_allocation
  );
}

/* Our subscription lines that are NOT on the given cadence. This, and not a
   sample of one line, is what decides whether the cart needs moving. */
function linesOffCadence(data, wantedMonths) {
  return subLines(data).filter((line) => lineMonths(line) !== wantedMonths).length;
}

/* The cart's cadence in months, or 0 with nothing subscribed.

   It used to be the first subscription line's cadence and nothing else, and
   that one sample was treated as the whole cart's. A cart could carry three
   lines on two different plans, the sync would ask "is the cart already on
   three months", look at whichever line Shopify listed first, and skip: two
   lines were left on the wrong plan indefinitely. Now: if the lines agree,
   that cadence; if they do not, the one most of them are on, which is what
   the dropdown shows while the sync brings the rest across. Nothing that
   decides whether to move the cart reads this any more; see linesOffCadence. */
function subIntervalMonths(data) {
  const lines = subLines(data);
  if (!lines.length) return 0;
  const tally = {};
  lines.forEach((line) => {
    const m = lineMonths(line);
    tally[m] = (tally[m] || 0) + line.quantity;
  });
  return Number(Object.keys(tally).sort((a, b) => tally[b] - tally[a])[0]);
}

/* Delivery interval follows the bag count. One bag is one person's month, so
   three bags on a monthly plan means the next delivery lands while two are
   still unopened. When the shopper changes quantity we move the interval to
   match, which is the right answer most of the time.

   A deliberate choice always wins. Once the Delivery select is used we stop
   for the rest of the session rather than quietly undoing what someone just
   picked. sessionStorage because the cart outlives this drawer, with a memory
   flag behind it because private-mode browsers throw on access rather than
   returning null. */
const PINNED_KEY = 'wgCartIntervalPinned';
let pinnedInMemory = 0;

/* The pinned cadence in months, or 0 when the cart follows its bag count. */
function intervalPinned() {
  try {
    return Number(sessionStorage.getItem(PINNED_KEY)) || 0;
  } catch (error) {
    return pinnedInMemory;
  }
}

function pinInterval(monthsOrOff) {
  const value = Number(monthsOrOff) || 0;
  pinnedInMemory = value;
  try {
    if (value) sessionStorage.setItem(PINNED_KEY, String(value));
    else sessionStorage.removeItem(PINNED_KEY);
  } catch (error) {
    /* the memory flag above still carries it for this page */
  }
}

/* How many people the bags are for. The buy box knows, because the shopper
   told it, and hands it over with every add. A couple on a one month supply
   holds two bags and wants them monthly; without this the drawer read two
   bags as two months and re-planned the order on the first press. Lives
   with the pin, and is reset with it. */
const HOUSEHOLD_KEY = 'wgCartHousehold';
let householdInMemory = 1;

function household() {
  try {
    return Math.max(1, Number(sessionStorage.getItem(HOUSEHOLD_KEY)) || 1);
  } catch (error) {
    return householdInMemory;
  }
}

function setHousehold(people) {
  const value = Math.max(1, Number(people) || 1);
  householdInMemory = value;
  try {
    if (value > 1) sessionStorage.setItem(HOUSEHOLD_KEY, String(value));
    else sessionStorage.removeItem(HOUSEHOLD_KEY);
  } catch (error) {
    /* memory carries it for this page */
  }
}

/* The one cadence the whole cart should be on: the shopper's pinned choice
   when they made one, otherwise the plan that fits the bag count. Every
   automatic move and every projection sizes itself from this, so the cart
   converges on one plan rather than on whichever line was looked at. */
function wantedMonths(data) {
  return intervalPinned() || monthsForBags(cartStats(data).subBags);
}

/* The same move, locally, so the panel can repaint before a single request
   goes out. Only the plan's id: its display name is the server's to give, and
   inventing one here would swap every line's label for a different string and
   then swap it back a second later. */
function localInterval(data, months) {
  const planId = (CONFIG.planIds || {})[String(months)];
  if (!data || !planId) return data;
  data.__wgLocal = true;
  (data.items || []).forEach((item) => {
    const flavor = ourVariants[String(item.variant_id)];
    if (!flavor || !item.selling_plan_allocation) return;
    /* The name as the buy box knows it, so the rows read as one cadence
       rather than the id moving and the label lagging a write behind. */
    const known = planFor(flavor, months);
    item.selling_plan_allocation.selling_plan = Object.assign(
      {},
      item.selling_plan_allocation.selling_plan,
      { id: planId },
      known && known.planName ? { name: known.planName } : {}
    );
  });
  /* Two lines of one flavour on two plans are one line once they are on the
     same plan. Drawn as two, they carried the same identity on two rows,
     and a press on either was sent to whichever the server listed first. */
  mergeDuplicates(data);
  return data;
}

/* Fold lines that share an identity into the first of them, in place. The
   sum is what the shopper has of that flavour on that plan, and one row is
   the only honest way to draw it. */
function mergeDuplicates(data) {
  const seen = {};
  const kept = [];
  (data.items || []).forEach((item) => {
    const identity = lineIdentity(item);
    const first = seen[identity];
    if (!first) {
      seen[identity] = item;
      kept.push(item);
      return;
    }
    data.__wgLocal = true;
    const unit = first.original_price != null ? first.original_price : first.price;
    first.quantity += item.quantity;
    first.line_price = unit * first.quantity;
    first.original_line_price = unit * first.quantity;
    first.final_line_price = unit * first.quantity;
  });
  data.items = kept;
  return data;
}

/* Set what the shopper has of one identity, on the drawn cart: the first
   line takes the quantity, any other line of the same identity goes, since
   the row on screen was the sum of them. */
function localQuantityForIdentity(data, identity, quantity) {
  const items = data.items || [];
  const matches = items.filter((line) => lineIdentity(line) === identity);
  const first = matches[0] || findLineIn(items, identity);
  if (!first) return data;
  matches.slice(1).forEach((extra) => localQuantity(data, extra.key, 0));
  localQuantity(data, first.key, quantity);
  return data;
}

/* Every server line of one identity, exactly. More than one is a cart the
   server did not fold (or has not yet), and a quantity meant for the row
   they share belongs to the first once the rest are gone. */
function serverLinesExact(identity) {
  return ((serverCart && serverCart.items) || []).filter((line) => lineIdentity(line) === identity);
}

/* One run at a time, newest choice wins, the same bargain commitQuantity
   makes. A shopper who changes their mind mid-flight must not have two sets
   of writes racing each other through the same cart. pendingMonths keeps the
   chosen cadence on screen through every server response in between (see
   overlayPending), and a refused pass is tried once more before the drawer
   gives up, tells the shopper, and shows the cart as it really is. */
let intervalWanted = null;
let intervalInFlight = false;
let pendingMonths = null;
async function commitInterval(drawerEl, months) {
  intervalWanted = months;
  pendingMonths = months;
  /* Onto the drawn cart now, so whatever paints next (the offer handler's
     own repaint, a slow-write tick) shows one cadence, not the old lines
     beside the new bag for the length of the first write. */
  rebase();
  if (intervalInFlight) return;
  intervalInFlight = true;
  startIntervalSpin(drawerEl);
  let failures = 0;
  try {
    while (intervalWanted !== null) {
      const target = intervalWanted;
      intervalWanted = null;
      pendingMonths = target;
      if (await applyInterval(target)) {
        failures = 0;
        continue;
      }
      failures += 1;
      if (failures < 2) {
        await new Promise((resolve) => setTimeout(resolve, 800));
        if (intervalWanted === null) intervalWanted = target;
      } else {
        pendingMonths = null;
        /* The choice stays pinned. The refusal was the server's, not a
           change of mind, and the next press completes it. */
        rebase();
        const txt = CONFIG.txt || {};
        showError(drawerEl, lastIntervalRefusal === 429 ? txt.errorBusy : txt.error);
      }
    }
  } finally {
    intervalInFlight = false;
    pendingMonths = null;
    stopIntervalSpin(drawerEl);
    rebase();
    render(drawerEl);
  }
}

/* Move every one of our subscription lines that is not already on the given
   interval onto it. */
async function applyInterval(wanted) {
  const planId = (CONFIG.planIds || {})[String(wanted)];
  if (!planId || !serverCart) return false;
  /* Which lines need moving is read off the server's cart, never the drawn
     one: the drawn one has already been painted onto the target plan, so
     every line there claims to be done. The same read is what lets a line
     already on the plan be left alone, and what finds both lines when one
     flavour sits on two plans, which the drawn cart had collapsed into one
     identity and moved once. Each write rotates every key, so the key is
     read inside the lock immediately before its send. */
  const targets = subLines(serverCart)
    .filter((line) => lineMonths(line) !== wanted)
    .map((line) => lineIdentity(line));
  if (!targets.length) return true;
  try {
    for (const identity of targets) {
      await cartWrite('/cart/change.js', () => {
        const line = serverLineExact(identity);
        /* Gone since the list was taken, because a removal landed first, or
           already moved. Not an error, and not something to recreate. */
        if (!line) return null;
        return { id: line.key, quantity: line.quantity, selling_plan: planId };
      });
    }
  } catch (error) {
    lastIntervalRefusal = error && error.status;
    await fetchCart();
    return false;
  }
  return true;
}
let lastIntervalRefusal = null;

/* Run after anything that changes quantity. Silent when the shopper pinned
   their own interval, when nothing is subscribed, when the shop sells only
   one interval, or when the cart already sits on the right plan. */
/* Apply a quantity change to the held cart so the drawer can repaint before
   the server has heard about it. Mirrors the line maths Shopify returns:
   unit price comes from original_price where the line carries one, so a
   discounted line keeps its own unit rather than inheriting a tier price. */
function localQuantity(data, key, quantity) {
  const items = data.items || [];
  const index = items.findIndex((line) => line.key === key);
  if (index < 0) return data;
  const line = items[index];
  /* Mark the cart as ours rather than Shopify's. cartStats reads this and
     prices the whole cart from the tier table for as long as it stands, so a
     pressed line and its neighbours cannot end up on different tiers while
     the round trip is in flight. The next server response clears it by
     replacing the object. */
  data.__wgLocal = true;
  const unit = line.original_price != null ? line.original_price : line.price;
  const was = line.quantity;
  if (quantity === 0) {
    items.splice(index, 1);
  } else {
    line.quantity = quantity;
    line.line_price = unit * quantity;
    line.original_line_price = unit * quantity;
    line.final_line_price = unit * quantity;
  }
  data.item_count = Math.max(0, (data.item_count || 0) + (quantity - was));
  data.total_price = Math.max(0, (data.total_price || 0) + unit * (quantity - was));
  return data;
}

/* Shopify rotates a line's key on every change: the key that came back from
   /cart/add.js is dead the moment a quantity moves, and reusing it answers
   400 "no valid id or line parameter". So a queue cannot be addressed by
   key. Variant plus selling plan is the identity that survives, and the
   current key is looked up again from the freshest cart before each send. */
/* Shopify keeps two lines of the same variant apart when their line item
   properties differ, and merges them when they do not. An app that stamps a
   line (a bundle marker, a subscription reference) is relying on that. So
   the properties are part of what makes a line itself, and leaving them out
   of the identity meant two genuinely separate lines shared one row and one
   set of buttons, and the duplicate fold in commitQuantity would delete one
   of them to tidy up a duplicate that was never a duplicate. Hashed rather
   than spelled out, because this ends up in a data-did attribute that is
   then used as a CSS selector. Empty for a line carrying no properties,
   which is every line in an ordinary cart, so the identity of those is
   exactly what it always was. */
function propsKey(line) {
  const props = line && line.properties;
  if (!props || typeof props !== 'object') return '';
  const keys = Object.keys(props).filter((key) => props[key] !== null && props[key] !== '');
  if (!keys.length) return '';
  const flat = keys.sort().map((key) => key + '=' + String(props[key])).join('&');
  let hash = 5381;
  for (let i = 0; i < flat.length; i += 1) hash = ((hash * 33) ^ flat.charCodeAt(i)) >>> 0;
  return hash.toString(36);
}

function lineIdentity(line) {
  const plan = line.selling_plan_allocation && line.selling_plan_allocation.selling_plan;
  const props = propsKey(line);
  return String(line.variant_id) + ':' + (plan && plan.id ? plan.id : '0') + (props ? ':' + props : '');
}

/* A cheap "is this the same cart" stamp. Quantities and identities rather
   than keys, because Shopify rotates a line's key on every change and a
   key-based stamp would read as different every time. */
function cartSignature(snapshot) {
  if (!snapshot) return 'none';
  return (
    (snapshot.items || []).map((line) => lineIdentity(line) + 'x' + line.quantity).join(',') +
    '|' +
    (snapshot.total_price || 0)
  );
}

/* The line a button on screen is pointing at.

   Exact identity first. Failing that, the same variant on the same footing,
   subscription or one-time, because identity is variant plus selling plan and
   the interval sync rewrites the plan on every subscription line. That
   changes the identity of lines whose buttons are already rendered, and for
   as long as the sync runs, roughly one request per line, a press looked up
   an identity that no longer existed, found nothing and did nothing. The
   shopper pressed again, by which time the repaint had landed, and called it
   "it takes a couple of clicks".

   A press means "one more of this flavour". Which plan it happens to be on
   at that instant is not something the shopper chose or can see. */
function findLine(identity) {
  return findLineIn((cart && cart.items) || [], identity);
}

/* The same lookups against the server's cart, for anything about to send a
   key. findLine answers "which line is this button for"; these answer "what
   does the server call it right now". */
function findLineIn(items, identity) {
  const exact = items.find((line) => lineIdentity(line) === identity);
  if (exact) return exact;
  /* Failing that, the same variant on the same footing carrying the same
     properties. The plan is allowed to differ, because the cadence sync
     rewrites it under buttons that are already on screen; the properties are
     not, because a differently stamped line is a different line. */
  const parts = String(identity).split(':');
  const variantId = parts[0];
  const wantsPlan = parts.length > 1 && parts[1] !== '0';
  const wantsProps = parts.length > 2 ? parts[2] : '';
  return (
    items.find(
      (line) =>
        String(line.variant_id) === variantId &&
        !!line.selling_plan_allocation === wantsPlan &&
        propsKey(line) === wantsProps
    ) || null
  );
}

function serverLine(identity) {
  return findLineIn((serverCart && serverCart.items) || [], identity);
}

/* Exact identity only: variant and the plan it is on. For the cadence loop,
   where "the same flavour on any plan" is precisely the wrong answer. */
function serverLineExact(identity) {
  return ((serverCart && serverCart.items) || []).find((line) => lineIdentity(line) === identity) || null;
}

function serverKeyFor(identity) {
  /* Still null once the line is gone from the cart, which is the normal end
     of a remove: nothing of that variant is left on that footing to match. */
  const hit = serverLine(identity);
  return hit ? hit.key : null;
}

/* What the shopper has pressed that the server has not yet confirmed, held
   by identity from the press until the write lands. Laid over every server
   cart before it is drawn, so no response can paint a quantity the shopper
   has already moved past: a response for peach carries grape's old quantity,
   and grape's press is still on screen. Quantities and the cadence. */
const pendingQty = {};
function overlayPending(data) {
  /* The cadence first, because it can fold two lines into one, and a
     pressed quantity is the quantity of the folded row. */
  if (pendingMonths !== null) localInterval(data, pendingMonths);
  Object.keys(pendingQty).forEach((identity) => {
    localQuantityForIdentity(data, identity, pendingQty[identity]);
  });
  expectedLines.forEach((expected) => {
    if (!findLineIn(data.items || [], expected.identity)) {
      data.__wgLocal = true;
      data.items = (data.items || []).concat([expected.line]);
      data.item_count = (data.item_count || 0) + expected.line.quantity;
    }
  });
}

/* Lines the buy box has just sent and the server has not yet answered for.
   Drawn as settling rows, so the drawer can open on the press rather than
   after the round trip, and open onto the bag being added rather than onto
   "your cart is empty". Cleared by the add landing (noteAdd) or failing. */
let expectedLines = [];
function expectLines(list) {
  expectedLines = [];
  (list || []).forEach((entry) => {
    const flavor = ourVariants[String(entry.variantId)];
    if (!flavor) return;
    const planId = entry.planId ? String(entry.planId) : '';
    const unit = flavor.priceCents || 0;
    const quantity = Math.max(1, Number(entry.quantity) || 1);
    expectedLines.push({
      identity: String(entry.variantId) + ':' + (planId || '0'),
      line: {
        key: 'pending-' + entry.variantId + '-' + (planId || '0'),
        variant_id: Number(entry.variantId),
        product_id: flavor.productId || 0,
        product_title: flavor.label || '',
        quantity: quantity,
        image: flavor.image || '',
        price: unit,
        original_price: unit,
        line_price: unit * quantity,
        original_line_price: unit * quantity,
        final_line_price: unit * quantity,
        selling_plan_allocation: planId ? { selling_plan: { id: Number(planId), name: entry.planName || '' } } : null,
      },
    });
  });
  rebase();
  /* Drawn now, so a drawer opened on the press already shows them. */
  const drawer = document.querySelector('cart-drawer[data-wg-cart]');
  if (drawer && cart) render(drawer);
  /* An add the buy box gives up on after thirty seconds must not hold the
     checkout for ever. */
  clearTimeout(expectTimer);
  if (expectedLines.length) {
    expectTimer = setTimeout(() => {
      expectedLines = [];
      rebase();
      if (drawer && cart) render(drawer);
    }, 35000);
  }
}
let expectTimer = null;

/* Flavours whose empty-state add has not landed yet. */
const tileAdding = {};

/* One request in flight per line, newest quantity wins. Tapping + four times
   paints four times and sends far fewer, because /cart/change.js takes an
   absolute quantity: whatever goes last is the truth. */
const inFlight = {};
const wanted = {};

/* One quantity write, retried while Shopify is only asking us to slow down.
   keyAt is called inside the write lock so the key is read at the last
   possible moment; null from it means the line is gone, which is the normal
   end of a remove. */
async function sendQuantity(drawerEl, identity, quantity, keyAt) {
  let busy = 0;
  for (;;) {
    try {
      return await cartWrite('/cart/change.js', () => {
        const key = keyAt();
        return key ? { id: key, quantity: quantity } : null;
      });
    } catch (error) {
      if (error && error.status === 429 && busy < BUSY_RETRIES) {
        busy += 1;
        watchForSlow(drawerEl, identity);
        await busyPause(busy);
        continue;
      }
      throw error;
    }
  }
}

/* 429 is Shopify asking for a slower hand, not refusing the change. Five
   taps coalesce into one write carrying the absolute quantity, so throwing
   that write away throws away all five taps at once: the drawer counts up
   to six, the one request is refused, and the panel drops back to two. The
   shopper did nothing wrong and their presses are still true, so the write
   is simply sent again after a pause, with what they pressed left on screen
   throughout. Only when it keeps being refused does the drawer give up and
   say so. */
const BUSY_RETRIES = 3;
const busyPause = (attempt) => new Promise((resolve) => setTimeout(resolve, 700 * attempt + 300));

async function commitQuantity(drawerEl, identity, quantity) {
  wanted[identity] = quantity;
  pendingQty[identity] = quantity;
  if (inFlight[identity]) return;
  inFlight[identity] = true;
  try {
    while (wanted[identity] !== undefined) {
      /* Never interleaved with a cadence move. The move folds lines and
         rotates keys as it goes, and a quantity sent into the middle of
         that landed on whichever line the server had at that instant. It
         is short, one request per line, and the press stays on screen. */
      while (intervalInFlight || intervalWanted !== null) {
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
      const target = wanted[identity];
      delete wanted[identity];
      /* One row can be more than one server line, so the row's total is
         spread across them rather than collapsed onto one. Ordinary carts
         have exactly one line here and this is the single write it always
         was. Where there are several: only the lines whose quantity actually
         changes are written, increases go first and decreases run backwards
         from the end, so a refusal part way can only ever leave the row
         reading high, never lower than the shopper started with. Deleting a
         line first and setting the quantity second, which is what this used
         to do, meant one refused write destroyed bags and told the shopper
         nothing had changed. */
      const lines = serverLinesExact(identity);
      let data = null;
      if (lines.length < 2) {
        data = await sendQuantity(drawerEl, identity, target, () => serverKeyFor(identity));
      } else {
        let left = target;
        const plan = lines.map((line) => {
          const give = Math.min(left, line.quantity);
          left -= give;
          return { was: line.quantity, want: give };
        });
        if (left > 0) plan[0].want += left;
        const ups = [];
        const downs = [];
        plan.forEach((step, index) => {
          if (step.want > step.was) ups.push(index);
          else if (step.want < step.was) downs.push(index);
        });
        for (const index of ups) {
          data = await sendQuantity(drawerEl, identity, plan[index].want, () => {
            const now = serverLinesExact(identity)[index];
            return now ? now.key : null;
          }) || data;
        }
        /* backwards, so removing one does not shift the next one's place */
        for (const index of downs.reverse()) {
          data = await sendQuantity(drawerEl, identity, plan[index].want, () => {
            const now = serverLinesExact(identity)[index];
            return now ? now.key : null;
          }) || data;
        }
        if (!ups.length && !downs.length) data = serverCart;
      }
      if (!data) {
        /* The line is gone from the server. After a remove that is the end
           of the job. After a + it is a button that outlived its line (the
           switch offer just converted it, or another tab removed it), and
           the shopper is looking at a quantity nothing will deliver: take it
           back and say so. */
        if (target > 0) {
          const gone = new Error('gone');
          throw gone;
        }
        break;
      }
      /* Landed, and nothing newer pressed: stop laying the old target over
         fresh carts. */
      if (wanted[identity] === undefined) {
        delete pendingQty[identity];
        rebase();
      }
    }
    render(drawerEl);
    scheduleIntervalSync(drawerEl);
  } catch (error) {
    /* The optimistic view is now a lie, so take it back rather than leave a
       quantity the server never accepted sitting on screen. But say so. Taking
       it back in silence is what made a refused write look like the drawer
       misbehaving: a removed line reappearing a second later, or a quantity
       climbing to five and dropping back to three, with nothing to explain
       either. 429 is Shopify rate limiting the cart, which is its own message
       because the answer to it is to wait rather than to try harder. */
    delete wanted[identity];
    delete pendingQty[identity];
    await fetchCart();
    rebase();
    render(drawerEl);
    const txt = CONFIG.txt || {};
    showError(drawerEl, error && error.status === 429 ? txt.errorBusy : txt.error);
  } finally {
    delete inFlight[identity];
    delete pendingQty[identity];
    stopWatching(drawerEl, identity);
  }
}

/* The interval sync costs one request per subscription line, so awaiting it
   before painting made every quantity press feel like it took seconds: the
   change itself, then a round trip per line, with all the steppers disabled
   throughout. Measured at 3.2s for a two flavour cart.

   The shopper's press is now answered immediately and the interval settles
   behind it. Debounced because tapping + three times should re-plan once, at
   the end, not once per tap, and it only repaints when the interval actually
   moved so a settled cart never flickers. */
let intervalSyncTimer = null;
function scheduleIntervalSync(drawerEl) {
  clearTimeout(intervalSyncTimer);
  intervalSyncTimer = setTimeout(() => runIntervalSync(drawerEl), 600);
}

/* The timer's body, callable on its own so checkout can run a sync that is
   still waiting on its timer rather than wait out the timer. Nulling the
   handle first is what lets pendingWork tell "a sync is due" from "a sync is
   running": commitInterval raises its own flag before the first await. */
async function runIntervalSync(drawerEl) {
  clearTimeout(intervalSyncTimer);
  intervalSyncTimer = null;
  await syncIntervalToBags();
  /* Repaint on any change, not just a changed interval. Rewriting a line's
     selling plan changes what that line is, and the buttons on screen still
     describe the old one. Testing the interval alone missed the partial
     case, where some lines were rewritten and the reported interval came
     back the same. */
  if (cartSignature(cart) !== paintedSignature) render(drawerEl);
}

/* Everything the drawer may still owe the server: a press whose request has
   not returned, a quantity coalesced behind it, a cadence move mid-loop, or
   a sync still waiting on its timer. */
function pendingWork(includeOffer) {
  return (
    Object.keys(inFlight).length > 0 ||
    Object.keys(wanted).length > 0 ||
    intervalInFlight ||
    intervalWanted !== null ||
    intervalSyncTimer !== null ||
    (!!includeOffer && offerBusy) ||
    addsSettling > 0 ||
    expectedLines.length > 0 ||
    writesInFlight > 0
  );
}

/* An add from outside the drawer has landed: the buy box, or Dawn's product
   form. The household comes with it, the cart is re-read, and the cadence
   sync is scheduled so a cart built from two different sizes ends on one
   plan rather than sitting mixed until someone presses a stepper. Counted as
   pending work until the sync is on its timer, so a checkout pressed on the
   heels of the add waits for it. */
let addsSettling = 0;
async function noteAdd(info) {
  addsSettling += 1;
  try {
    if (info && info.household) setHousehold(info.household);
    expectedLines = [];
    /* settled: the cart was empty before this add, so it now holds nothing
       but the lines the buy box just sent, each on the plan that order's own
       size implies. There is provably nothing to read back and nothing to
       move, and making the shopper wait a round trip to be told so is the
       one case where this reconcile buys nothing. The cart is still refreshed
       behind them, for the times they do not go anywhere. */
    if (info && info.settled) {
      fetchCart().then(() => {
        const el = document.querySelector('cart-drawer[data-wg-cart]');
        if (el) render(el);
      });
      return;
    }
    await fetchCart();
    const drawer = document.querySelector('cart-drawer[data-wg-cart]');
    if (drawer) {
      render(drawer);
      scheduleIntervalSync(drawer);
    }
  } finally {
    addsSettling -= 1;
  }
}

/* Resolves once the server holds the cart the drawer shows, or after maxMs,
   whichever is first. A sync still on its timer is started now, not waited
   for. */
function settle(drawerEl, maxMs, includeOffer) {
  const deadline = Date.now() + (maxMs || 10000);
  return new Promise((resolve) => {
    const tick = () => {
      /* The timer is set by the quantity write as it lands, so it may appear
         part way through the wait. Run it the moment it does. */
      if (intervalSyncTimer !== null) runIntervalSync(drawerEl);
      if (!pendingWork(includeOffer) || Date.now() >= deadline) return resolve(!pendingWork(includeOffer));
      setTimeout(tick, 50);
    };
    tick();
  });
}

/* The order is whatever the server holds when the checkout page loads, not
   what the drawer shows. Leaving with writes still owed ships a different
   cart from the one on screen: a coalesced quantity is never sent at all,
   and a sync that has not fired leaves three bags on a monthly plan. So the
   button waits for the drawer to settle, says so, and only then goes.
   Bounded, because a cart Shopify is throttling could hold it for a long
   time and a checkout that never comes is worse than one a bag short.

   The buy box passes its own beforeLeave when it owns the button: richer
   items and the event id its GA and Meta pair share. It receives the settled
   cart, so what it reports is what the checkout will show. */
const SETTLE_MS = 10000;
let leaving = false;
let leaveEpoch = 0;
async function goToCheckout(drawerEl, beforeLeave) {
  if (leaving) return;
  /* An empty cart has nowhere to go; Shopify would bounce it back. */
  if (cart && !(cart.items || []).length && !pendingWork(true)) return;
  leaving = true;
  const epoch = leaveEpoch;
  const button = drawerEl && drawerEl.querySelector('[data-drawer-checkout]');
  const txt = CONFIG.txt || {};
  if (pendingWork(true)) {
    /* aria-disabled, not disabled: a disabled element cannot hold focus,
       and the keyboard user who pressed this would be dropped onto the
       document behind the dialog for the length of the wait. The leaving
       flag is what refuses a second press. */
    if (button) {
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('wg-drawer__checkout--wait');
      if (txt.checkoutWait) button.textContent = txt.checkoutWait;
    }
    announce(drawerEl, txt.checkoutWait || '');
    await settle(drawerEl, SETTLE_MS, true);
    /* The page was left and restored while this waited (a back from
       checkout through the bfcache); the press it was for is history. */
    if (epoch !== leaveEpoch) return;
  }
  /* Settled. Repaint with everything disabled, so nothing can start a write
     inside the window the pixels get to flush. */
  render(drawerEl);
  try {
    if (typeof beforeLeave === 'function') beforeLeave(cart);
    else track('begin_checkout', cart);
  } catch (error) {
    /* tracking never stands between a shopper and the checkout */
  }
  /* Released before we go. A back button that misses the bfcache rebuilds
     the page from scratch, and a body still pinned at minus its old offset
     would put the shopper at the top of the store. */
  unlockPage();
  /* the window the pixels get to flush before the page goes */
  setTimeout(() => { window.location.href = '/checkout'; }, 250);
  /* Still here well after that means the navigation did not happen (a
     prompt held it, a redirect failed). A drawer with everything disabled
     is no use then; hand it back. */
  setTimeout(() => { if (leaving && epoch === leaveEpoch) unlockCheckout(drawerEl); }, 4000);
}

/* Back from checkout through the bfcache, the heap still says we are
   leaving. Put the button back. */
function unlockCheckout(drawerEl) {
  leaving = false;
  leaveEpoch += 1;
  const button = drawerEl && drawerEl.querySelector('[data-drawer-checkout]');
  if (!button) return;
  button.classList.remove('wg-drawer__checkout--wait');
  button.removeAttribute('aria-disabled');
  const txt = CONFIG.txt || {};
  if (txt.checkout) button.textContent = txt.checkout;
  render(drawerEl);
}

async function syncIntervalToBags() {
  if (!CONFIG.matchIntervalToBags || !serverCart) return;
  if (cartStats(serverCart).subBags === 0) {
    /* nothing subscribed: let the next subscription start unpinned */
    pinInterval(false);
    return;
  }
  if (planMonths().length < 2) return;
  /* The pinned cadence when there is one, the bag count's otherwise. A
     pinned cart is still brought onto its one plan: a bag added from the
     buy box on a different size, or a pass the server refused halfway,
     leaves lines off it, and "pinned" used to mean those were never
     touched again. Every line is counted, not a sample, and the move goes
     through commitInterval so an automatic move and a chosen one can never
     run their per-line writes over the top of each other. */
  const wanted = wantedMonths(serverCart);
  /* While a move is in flight the server's cart does not yet show it, so
     "nothing is off cadence" can be read off a line that is about to be
     moved the other way: a remove had scheduled a move to monthly, a press
     landed, and the cart settled on monthly with two bags. Hand the target
     to the running loop instead; it re-reads the cart after its pass. */
  if (!intervalInFlight && linesOffCadence(serverCart, wanted) === 0) return;
  await commitInterval(document.querySelector('cart-drawer[data-wg-cart]'), wanted);
}

/* One offer at a time. render() rebuilds the card, and with it the button,
   on every cart change, and a rebuilt button is an enabled one: a second
   press mid-switch used to run a second switch over the first. The flag
   outlives the button. */
let offerBusy = false;
/* A subscription switch whose added bags could not be taken back off after
   its removals were refused; see applyOffer. */
let switchLeftover = null;

async function applyOffer(offer) {
  const drawerEl = document.querySelector('cart-drawer[data-wg-cart]');
  /* Presses still on their way to the server are part of the cart the offer
     is about to act on. Wait for them, or a bag the shopper just added is
     switched at its old quantity and the queued write is later sent to a
     line that no longer exists. */
  await settle(drawerEl, SETTLE_MS);
  await fetchCart();
  if (!serverCart) throw new Error('cart unavailable');
  if (offer.kind === 'sub') {
    /* Size the plan to every bag that will be subscribed once the switch is
       done, one bag being a one month supply, unless the shopper pinned a
       cadence. The sync scheduled at the end brings anything else across. */
    /* Sized from the server's cart at the moment the add is built, inside
       the lock: a press that lands between the offer being pressed and its
       add going out is then part of the switch rather than lost to it. */
    const sizeTargets = () => {
      const stats = cartStats(serverCart);
      const wanted = intervalPinned() || monthsForBags(stats.subBags + stats.otpBags);
      const list = [];
      (serverCart.items || []).forEach((item) => {
        const flavor = ourVariants[String(item.variant_id)];
        if (!flavor || item.selling_plan_allocation) return;
        const plan = planFor(flavor, wanted) || planFor(flavor, months());
        if (!plan) return; /* nothing to switch it to: leave it one-time */
        list.push({ variant: String(item.variant_id), qty: item.quantity, planId: plan.planId });
      });
      return list;
    };
    let targets = sizeTargets();
    if (!targets.length) return;

    /* The subscriptions go on in one request, then the originals come off
       one by one, each looked up again at the moment of its send. Adding
       first means a failure part way leaves the shopper with more bags in
       the cart rather than fewer, and the rollback below takes the extra
       back off so a checkout in that state cannot bill them twice. What
       each plan line held before the add is what the rollback returns it
       to. */
    /* A switch whose rollback failed last time left its subscription bags
       in the cart. Matched by flavour, because a sync may have moved them
       to another plan since: a flavour the server already holds that many
       subscribed bags of is not added again, only its one-time line comes
       off. */
    const leftover = switchLeftover || [];
    const subHeld = (variant) =>
      subLines(serverCart)
        .filter((line) => String(line.variant_id) === variant)
        .reduce((sum, line) => sum + line.quantity, 0);
    /* The switch adds with /cart/add.js and no properties of its own, so the
       line it creates is the unstamped one for that variant and plan. Asked
       for by those two facts rather than by a spelled-out identity, which
       also carries a properties part. */
    const addedLine = (variant, planId) =>
      ((serverCart && serverCart.items) || []).find(
        (line) =>
          String(line.variant_id) === String(variant) &&
          line.selling_plan_allocation &&
          String(line.selling_plan_allocation.selling_plan.id) === String(planId) &&
          !propsKey(line)
      ) || null;
    const markPrior = () => {
      targets.forEach((t) => {
        const prior = leftover.find((l) => l.variant === t.variant);
        const before = addedLine(t.variant, t.planId);
        t.baseline = before ? before.quantity : 0;
        t.skipAdd = !!prior && subHeld(t.variant) >= (prior.baseline || 0) + prior.qty;
        if (t.skipAdd) t.baseline = prior.baseline || 0;
      });
    };
    await cartWrite('/cart/add.js', () => {
      targets = sizeTargets();
      markPrior();
      const adding = targets.filter((t) => !t.skipAdd);
      if (!adding.length) return null;
      return { items: adding.map((t) => ({ id: +t.variant, quantity: t.qty, selling_plan: t.planId })) };
    });
    if (!targets.length) return;
    const switched = {};
    targets.forEach((t) => { switched[t.variant] = true; });
    const done = {};
    const stale = () =>
      ((serverCart && serverCart.items) || []).filter(
        (item) => switched[String(item.variant_id)] && !done[String(item.variant_id)] && !item.selling_plan_allocation
      );
    let refused = null;
    /* Two passes, then two rollback attempts: under a standing 429 every
       extra write extends the throttle, and the message is what helps. */
    for (let pass = 0; pass < 2 && stale().length; pass++) {
      for (const line of stale()) {
        const identity = lineIdentity(line);
        const switched = targets.find((t) => t.variant === String(line.variant_id));
        try {
          await cartWrite('/cart/change.js', () => {
            const now = serverLineExact(identity);
            if (!now) return null;
            /* Only what was switched comes off. A bag pressed onto the
               one-time line since is left there, still the shopper's. */
            const keep = Math.max(0, now.quantity - (switched ? switched.qty : now.quantity));
            return { id: now.key, quantity: keep };
          });
          done[String(line.variant_id)] = true;
        } catch (error) {
          refused = error;
          await fetchCart();
        }
      }
    }
    if (stale().length) {
      /* Take the added subscription bags back off: by quantity, so a bag the
         shopper already had on that plan is left alone. Tried a few times
         with a pause, because what refused the removals (429, a dropped
         connection) is likely to refuse this too for a moment. If it still
         fails, remember what was added, so a retry of the switch does not
         add it a second time. Then say so. */
      let undone = false;
      for (let attempt = 0; attempt < 2 && !undone; attempt++) {
        if (attempt) await new Promise((resolve) => setTimeout(resolve, 700 * attempt));
        undone = true;
        for (const t of targets) {
          try {
            await cartWrite('/cart/change.js', () => {
              const now = addedLine(t.variant, t.planId);
              return now && now.quantity > (t.baseline || 0) ? { id: now.key, quantity: t.baseline || 0 } : null;
            });
          } catch (error) {
            undone = false;
          }
        }
      }
      switchLeftover = undone ? null : targets;
      await fetchCart();
      const incomplete = new Error('switch incomplete');
      incomplete.status = refused && refused.status;
      throw incomplete;
    }
    switchLeftover = null;
  } else {
    const anchor = subLines(serverCart)[0];
    if (!anchor) return;
    /* The plan for the bag being added is the cadence the cart will want
       once it is in, not whatever the first line happens to be on. Copying
       the anchor is how a cart ended up on two plans at once: a removal had
       scheduled a move to two months, the press landed before it finished,
       and the new bag copied a three month plan off a line that was about to
       change. A pinned choice is the exception, since it is a choice. */
    const stats = cartStats(serverCart);
    const addMonths = intervalPinned() || monthsForBags(stats.subBags + 1);
    const addFlavor = offer.flavor || ourVariants[String(anchor.variant_id)];
    const addPlan = planFor(addFlavor, addMonths) || planFor(addFlavor, subIntervalMonths(serverCart));
    const planId = addPlan
      ? addPlan.planId
      : anchor.selling_plan_allocation.selling_plan && anchor.selling_plan_allocation.selling_plan.id;
    const variant = offer.flavor ? offer.flavor.variantId : anchor.variant_id;
    await cartWrite('/cart/add.js', () => ({ items: [{ id: variant, quantity: 1, selling_plan: planId }] }));
  }
  /* Whatever kind, anything the add left off the cart's one cadence is
     brought across now, not after the stepper debounce: a single press is
     not a burst. Not awaited, so the card answers as soon as the add has
     landed; commitInterval paints the target cadence over every line from
     its first tick, so the shopper never sees two cadences side by side.
     The checkout gate counts the move as pending work. */
  if (CONFIG.matchIntervalToBags && serverCart && planMonths().length > 1) {
    const target = wantedMonths(serverCart);
    if (linesOffCadence(serverCart, target) > 0) commitInterval(drawerEl, target);
  }
  try {
    if (!window.Shopify || !window.Shopify.designMode) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'wg_drawer_upsell', upsell_kind: offer.kind });
    }
  } catch (error) {
    /* tracking must never break the cart */
  }
}

/* Confetti for the top tier. Hand rolled rather than pulled from a CDN: a
   library for twenty coloured divs is not worth the kilobytes on a DTC cart.
   Fires once per drawer session, so re-rendering on every quantity change does
   not restage it, and does nothing at all under prefers-reduced-motion. */
let celebrated = false;
let celebrationPending = false;
function celebrate(drawer) {
  if (celebrated) return;
  /* The panel is painted while hidden, on load and after every cart change,
     so a render is not proof that anyone is looking. Firing here would spend
     the one-shot flag on an invisible drawer and the shopper would never see
     it. Park it for the next open instead. */
  if (!drawer.classList.contains('open')) {
    celebrationPending = true;
    return;
  }
  celebrated = true;
  try {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const host = drawer.querySelector('.wg-drawer__panel');
    if (!host) return;
    const layer = document.createElement('div');
    layer.className = 'wg-confetti';
    layer.setAttribute('aria-hidden', 'true');
    const colors = ['#F9EB12', '#004225', '#ffffff', '#F2A86B'];
    for (let i = 0; i < 22; i += 1) {
      const bit = document.createElement('i');
      bit.style.left = (4 + (i * 92) / 22) + '%';
      bit.style.background = colors[i % colors.length];
      bit.style.animationDelay = (i % 7) * 60 + 'ms';
      bit.style.transform = 'rotate(' + (i * 37) % 360 + 'deg)';
      layer.appendChild(bit);
    }
    host.appendChild(layer);
    setTimeout(() => { layer.remove(); }, 2600);
  } catch (error) {
    /* decoration must never break the cart */
  }
}

/* What the footer prints and what the analytics events report: our lines at
   the tier price, anything else at the cart's own price, so a mixed cart
   still adds up to what checkout will charge. */
function displayTotalCents(data, stats) {
  /* Anything that is not ours is priced line by line off what Shopify last
     said it costs, so a press on one of our lines cannot move it. Deriving
     it as "cart total minus our lines" did: on a locally painted frame the
     cart total had moved by a full bag while our lines moved by a
     discounted one, and a foreign line shrank by a bag's discount per
     press. */
  let foreign = 0;
  ((data && data.items) || []).forEach((item) => {
    if (ourVariants[String(item.variant_id)]) return;
    foreign += item.final_line_price != null ? item.final_line_price : item.line_price || 0;
  });
  return stats.subPay + stats.otpPay + foreign;
}

/* Lines whose write has been in flight long enough to be worth mentioning.
   Held by identity rather than as a class on the node, because render()
   rebuilds the rows and a class would not survive the repaint.

   Nothing is shown for the first 600ms on purpose. Almost every write settles
   inside that, and a spinner on every press would undo the instant feel the
   optimistic paint exists to give. This is for the slow ones, which used to
   look like nothing was happening. */
const slowWrites = new Set();
const slowTimers = {};

function watchForSlow(drawerEl, identity) {
  clearTimeout(slowTimers[identity]);
  slowTimers[identity] = setTimeout(() => {
    slowWrites.add(identity);
    render(drawerEl);
  }, 600);
}

function stopWatching(drawerEl, identity) {
  clearTimeout(slowTimers[identity]);
  delete slowTimers[identity];
  if (slowWrites.delete(identity)) render(drawerEl);
}

/* Changing the cadence is one request per subscription line, so it is the
   longest wait in the drawer and the one with the most to mislead with: the
   select shows the new value the instant it is picked, while the cart is
   still on the old one for a second or two afterwards. So the value is not
   shown at all until it is true. The spinner stands in its place, inside
   the control, and the control does not change size while it is there.

   Immediately, not after a courtesy delay like the lines: a spinner that
   waits is a spinner that lets the shopper read a number the cart has not
   agreed to yet, which is the whole thing this is here to stop. */
let intervalSlow = false;

function startIntervalSpin(drawerEl) {
  if (intervalSlow) return;
  intervalSlow = true;
  render(drawerEl);
}

function stopIntervalSpin(drawerEl) {
  if (!intervalSlow) return;
  intervalSlow = false;
  render(drawerEl);
}

/* A refused cart write, said out loud. Shown in the commit bar and announced,
   then cleared, because it describes a moment rather than a state. */
let errorTimer = null;
function showError(drawerEl, message) {
  const el = drawerEl && drawerEl.querySelector('[data-drawer-err]');
  if (!el || !message) return;
  el.textContent = message;
  /* role=alert announces this on its own; the polite region would read the
     same sentence a second time straight after. */
  el.hidden = false;
  clearTimeout(errorTimer);
  errorTimer = setTimeout(() => { el.hidden = true; }, 6000);
}

/* A press repaints the lines and moves the total without a word. This is the
   only part of that a screen reader hears, so it fires on deliberate changes
   and never on the background syncs. */
function announce(drawerEl, message) {
  const live = drawerEl && drawerEl.querySelector('[data-drawer-live]');
  if (!live || !message) return;
  /* The same string twice running is not re-announced, and pressing + twice
     produces exactly that. Clearing first makes the second one land. */
  live.textContent = '';
  setTimeout(() => { live.textContent = message; }, 60);
}

/* One tap out of the empty state. One-time on purpose: it is the smaller ask,
   and the drawer's own subscribe offer picks the shopper up the moment the
   line lands. */
async function addVariant(variantId) {
  await cartWrite('/cart/add.js', () => ({ items: [{ id: Number(variantId), quantity: 1 }] }));
}

/* GA4 ecommerce, dataLayer only. The buy box owns the gtag and pixel fan-out
   when it is on the page, and a second direct call would double count. */
function track(eventName, data) {
  try {
    if (window.Shopify && window.Shopify.designMode) return;
    const items = (data && data.items) || [];
    if (!items.length) return;
    const stats = cartStats(data);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: eventName,
      ecommerce: {
        currency: CONFIG.currency || 'USD',
        value: +(displayTotalCents(data, stats) / 100).toFixed(2),
        items: items.map((line) => ({
          item_id: String(line.product_id || ''),
          item_variant: String(line.variant_id || ''),
          item_name: line.product_title || '',
          quantity: line.quantity,
          price: +(((line.original_price != null ? line.original_price : line.price) || 0) / 100).toFixed(2),
        })),
      },
    });
  } catch (error) {
    /* analytics must never break the cart */
  }
}

/* Focus on the document while the panel is open is focus that a repaint
   took away, or a button that was disabled under the finger (a disabled
   element cannot hold focus). On the document it is outside the dialog:
   Tab walks the page behind the scrim and Escape no longer closes
   anything. The stepper case restores itself to the same button; this
   catches the rest: the offer button, a tile, a remove that emptied the
   cart, the checkout button while it waits. */
function settleFocus(drawer) {
  if (!drawer.classList.contains('open')) return;
  const now = document.activeElement;
  /* Inside, and not inside something this paint just hid: the browser
     moves focus off a hidden element on its own, but only at the next
     rendering step, which is after this runs. */
  const hidden = now && now.closest && now.closest('[hidden]');
  if (now && now !== document.body && drawer.contains(now) && !hidden) return;
  const target =
    drawer.querySelector('[data-drawer-lines] [data-dq]') ||
    drawer.querySelector('[data-drawer-lines] [data-dadd]') ||
    drawer.querySelector('.wg-drawer__x');
  if (target) target.focus({ preventScroll: true });
}

/* ---------- render ---------- */
function render(drawer) {
  const txt = CONFIG.txt || {};
  const q = (selector) => drawer.querySelector(selector);
  const lines = q('[data-drawer-lines]');
  const totalEl = q('[data-drawer-total]');
  const shipEl = q('[data-drawer-ship]');
  const fill = q('[data-dprog-fill]');
  const checkout = q('[data-drawer-checkout]');
  const saveEl = q('[data-drawer-save]');
  const offerEl = q('[data-drawer-offer]');
  const freqEl = q('[data-drawer-freq]');
  if (!lines) return;
  /* Stamp what this paint puts on screen. open() compares the stamp against
     the live cart, so it repaints exactly when the panel is out of date and
     never because some other code path happened to refresh the cart without
     drawing it. Tracking the cart instead of the paint would miss precisely
     that case. */
  paintedSignature = cartSignature(cart);

  /* The count belongs in the title: the header bubble is behind the panel
     once this is open. */
  const countEl = drawer.querySelector('[data-drawer-count]');
  if (countEl) {
    const count = (cart && cart.item_count) || 0;
    countEl.hidden = !count;
    countEl.textContent = count ? '(' + count + ')' : '';
  }

  if (!cart) {
    drawer.classList.add('wg-drawer--empty');
    lines.innerHTML =
      '<p class="wg-drawer__fallback">' + esc(txt.fallback || '') +
      ' <a href="/cart">' + esc(txt.fallbackLink || 'View cart') + '</a></p>';
    if (offerEl) offerEl.hidden = true;
    if (saveEl) saveEl.hidden = true;
    if (freqEl) freqEl.hidden = true;
    if (totalEl) totalEl.textContent = '';
    if (shipEl) shipEl.textContent = '';
    if (checkout) checkout.disabled = true;
    return;
  }

  const stats = cartStats(cart);
  const items = cart.items || [];

  /* An empty drawer that only says "your cart is empty" is a dead end, and
     the footer's zeroed money rows make it look broken as well. The class
     clears those; the flavors below give it somewhere to go. */
  drawer.classList.toggle('wg-drawer--empty', !items.length);

  if (!items.length) {
    const tiles = FLAVORS.filter((flavor) => flavor.available).slice(0, 3);
    lines.innerHTML =
      '<div class="wg-dempty">' +
      '<p class="wg-dempty__title">' + esc(txt.empty || '') + '</p>' +
      (txt.emptySub ? '<p class="wg-dempty__sub">' + esc(txt.emptySub) + '</p>' : '') +
      (tiles.length
        ? '<div class="wg-dempty__tiles">' +
          tiles
            .map(
              (flavor) =>
                '<button type="button" class="wg-dempty__tile" data-dadd="' + esc(flavor.variantId) + '"' +
                (tileAdding[String(flavor.variantId)] || leaving ? ' aria-disabled="true"' : '') + '>' +
                (flavor.image
                  ? '<img src="' + esc(flavor.image) + '" alt="" width="64" height="64" loading="lazy">'
                  : '') +
                '<span class="wg-dempty__name">' +
                esc((flavor.emoji ? flavor.emoji + ' ' : '') + (flavor.label || '')) +
                '</span>' +
                '<span class="wg-dempty__cta">' + esc(txt.emptyCta || '') + '</span>' +
                '</button>'
            )
            .join('') +
          '</div>'
        : '') +
      '</div>';
    drawer.querySelectorAll('[data-dadd]').forEach((button) => {
      button.onclick = async () => {
        /* A second tap on a flavour already on its way is a double tap, not
           an order for two; a tap on another flavour is another add and
           queues behind the first. The button keeps focus while it runs. */
        const variant = String(button.dataset.dadd);
        if (tileAdding[variant] || leaving) return;
        tileAdding[variant] = true;
        button.setAttribute('aria-disabled', 'true');
        try {
          await addVariant(button.dataset.dadd);
        } catch (error) {
          /* Nothing was added, so say so rather than repaint an empty cart
             and leave the tap looking ignored. */
          const note = document.createElement('p');
          note.className = 'wg-dempty__err';
          note.textContent = (error && error.status === 429 ? txt.errorBusy : txt.error) || '';
          const host = lines.querySelector('.wg-dempty');
          if (host && !host.querySelector('.wg-dempty__err')) host.appendChild(note);
          delete tileAdding[variant];
          button.removeAttribute('aria-disabled');
          return;
        }
        delete tileAdding[variant];
        render(drawer);
        announce(
          drawer,
          String(txt.liveUpdate || '').replace('[amount]', money(displayTotalCents(cart, cartStats(cart))))
        );
      };
    });
    if (checkout) checkout.disabled = true;
  } else {
    if (checkout) {
      checkout.disabled = false;
      if (leaving) checkout.setAttribute('aria-disabled', 'true');
      else checkout.removeAttribute('aria-disabled');
    }
    /* One-time and subscription lines are grouped rather than interleaved. A
       shopper scanning a mixed cart reads the first plan label and assumes it
       covers everything, so a lone one-time hides among subscriptions. It also
       gives each group a heading the upsell can speak to: convert the one-time
       group, or grow the subscription group. Headings only appear when both
       groups exist, since labelling a uniform cart says nothing. */
    const lineHtml = (item) => {
        const isSub = !!item.selling_plan_allocation;
        const planName = isSub
          ? (item.selling_plan_allocation.selling_plan && item.selling_plan_allocation.selling_plan.name) || txt.planLabel
          : txt.onceLabel;
        const flavor = ourVariants[String(item.variant_id)];
        /* What this line is charged, decided once in cartStats: Shopify's own
           figure when the cart carries the discount, the tier table when it
           does not. The row never prices anything itself, so the strike, the
           each line and the line total cannot drift from the subtotal. */
        const tiered = !!(flavor && isSub && CONFIG.priceSource === 'configured');
        const unitFull = tiered
          ? flavor.priceCents
          : fullUnit(item, item.original_price != null ? item.original_price : item.price);
        const lineFull = unitFull * item.quantity;
        const linePay = stats.linePay[lineIdentity(item)];
        const pay = linePay != null ? linePay : lineFull;
        /* Shopify discounts the bag, so this divides exactly on every real
           cart. Rounded up for the odd one that does not. */
        const unitPay = Math.ceil(pay / item.quantity);
        const priceHtml =
          pay < lineFull ? '<s>' + money(lineFull) + '</s>' + money(pay) : money(lineFull);
        /* Only when the line holds more than one, where the line total stops
           being the price of a bag. Struck when the bag is discounted: this
           is the number the shopper compares against the shelf, so showing
           the full price here without marking it down was the row quietly
           contradicting the total beside it. */
        const eachHtml =
          item.quantity > 1 && txt.eachLine
            ? '<div class="wg-dline__each">' +
              (unitPay < unitFull ? '<s>' + money(unitFull) + '</s>' : '') +
              esc(String(txt.eachLine).replace('[amount]', money(unitPay))) +
              '</div>'
            : '';
        /* Stars drawn from the rating Loox already stores on the product, so
           the cart carries the proof without carrying the review script. The
           overlay is clipped to the score rather than rounded to whole stars,
           because 4.8 rounded to five reads as a perfect score nobody has. */
        let rateHtml = '';
        if (CONFIG.showRating && flavor) {
          const score = Number(flavor.rating || 0);
          const count = Number(flavor.ratingCount || 0);
          if (score > 0) {
            const fill = Math.max(0, Math.min(100, (score / 5) * 100)).toFixed(1);
            const shown = String(txt.rateLine || '')
              .replace('[rating]', score.toFixed(1))
              .replace('[count]', count);
            const label = String(txt.rateLabel || '')
              .replace('[rating]', score.toFixed(1))
              .replace('[count]', count);
            /* On the plan line rather than under it. The plan label is two
               words on the widest cart and the row has the width spare, so
               the rating costs nothing in height, and height is what the
               smallest phone has none of: as its own row it took a cart from
               two whole lines on screen to one and a bit. */
            rateHtml =
              '<span class="wg-dline__rate"' + (label ? ' aria-label="' + esc(label) + '"' : '') + '>' +
              '<span class="wg-dline__stars" aria-hidden="true">★★★★★' +
              '<i style="width:' + fill + '%">★★★★★</i></span>' +
              (shown ? '<span class="wg-dline__ratenum" aria-hidden="true">' + esc(shown) + '</span>' : '') +
              '</span>';
          }
        }
        const name = flavor && flavor.label ? (flavor.emoji ? flavor.emoji + ' ' : '') + flavor.label : item.product_title;
        /* "Increase quantity, Lemon": three lines of identical button names
           are three buttons a screen reader cannot tell apart. */
        const plain = flavor && flavor.label ? flavor.label : item.product_title;
        const named = (label) => (label || '') + (plain ? ', ' + plain : '');
        const pending = String(item.key).indexOf('pending-') === 0;
        /* Nothing to press while the page is on its way to checkout. */
        const off = pending || leaving ? ' disabled' : '';
        /* A line still settling: one just added and not yet acknowledged, or
           one whose write has run long. */
        const busy = pending || slowWrites.has(lineIdentity(item)) ? ' busy' : '';
        /* Address the button by identity, not by key. A key is dead the
           moment anything touches that line, and this markup can outlive the
           cart it was drawn from: the background interval sync rewrites every
           line without necessarily repainting. Identity survives all of it. */
        const did = esc(lineIdentity(item));
        return (
          '<div class="wg-dline' + busy + '">' +
          (item.image ? '<img src="' + esc(item.image) + '" alt="">' : '') +
          '<div class="wg-dline__meta">' +
          '<div class="wg-dline__name">' + esc(name) + '</div>' +
          '<div class="wg-dline__plan">' + esc(planName) + rateHtml + '</div>' +
          eachHtml +
          '<div class="wg-dline__qty">' +
          '<button type="button"' + off + ' data-dq="-1" data-did="' + did + '" aria-label="' + esc(named(txt.qtyMinus)) + '">−</button>' +
          '<span>' + item.quantity + '</span>' +
          '<button type="button"' + off + ' data-dq="1" data-did="' + did + '" aria-label="' + esc(named(txt.qtyPlus)) + '">+</button>' +
          '<button type="button"' + off + ' class="wg-dline__rm" data-dq="0" data-did="' + did + '" aria-label="' + esc(named(txt.removeLabel)) + '">' + esc(txt.removeLabel || '') + '</button>' +
          '</div></div>' +
          '<div class="wg-dline__price">' + priceHtml + '</div>' +
          '</div>'
        );
    };
    /* One row per identity, whatever the server sent. */
    const drawn = mergeDuplicates({ items: JSON.parse(JSON.stringify(items)) }).items;
    const onceItems = drawn.filter((line) => !line.selling_plan_allocation);
    const subItems = drawn.filter((line) => !!line.selling_plan_allocation);
    const mixed = onceItems.length > 0 && subItems.length > 0;
    const group = (label, list) =>
      list.length
        ? (mixed && label ? '<div class="wg-dgroup__head">' + esc(label) + '</div>' : '') +
          '<div class="wg-dgroup">' + list.map(lineHtml).join('') + '</div>'
        : '';
    /* innerHTML replaces the button under the shopper's finger, and with it
       the keyboard focus, so a keyboard user could press + exactly once.
       Identity plus direction finds the same button in the new markup. */
    const wasOn = document.activeElement;
    const restore =
      wasOn && lines.contains(wasOn) && wasOn.dataset && wasOn.dataset.dq !== undefined
        ? { did: wasOn.dataset.did, dq: wasOn.dataset.dq }
        : null;

    lines.innerHTML = group(txt.groupOnce, onceItems) + group(txt.groupSub, subItems);

    drawer.querySelectorAll('[data-dq]').forEach((button) => {
      button.onclick = async () => {
        /* Identity, never a position and never a stored key: positions shift
           when another tab or an app merges the cart, and keys rotate on
           every change. The key used below is read back off the live cart. */
        const identity = button.dataset.did;
        const delta = +button.dataset.dq;
        const item = findLine(identity);
        if (!item) return;
        /* What the row shows: the sum of every drawn line of this identity,
           which is normally the one. */
        const held = (cart.items || [])
          .filter((line) => lineIdentity(line) === identity)
          .reduce((sum, line) => sum + line.quantity, 0) || item.quantity;
        const quantity = delta === 0 ? 0 : Math.max(0, held + delta);
        /* Paint from local state immediately. Every price in this drawer is
           computed here from the cart object and the tier table, so nothing
           on screen needs the server to be correct; only the cart itself
           does. Waiting on a round trip made a tap feel like a network
           operation, which is the whole difference next to a rival drawer. */
        localQuantityForIdentity(cart, identity, quantity);
        render(drawer);
        commitQuantity(drawer, identity, quantity);
        watchForSlow(drawer, identity);
        announce(
          drawer,
          String(txt.liveUpdate || '').replace(
            '[amount]',
            money(displayTotalCents(cart, cartStats(cart)))
          )
        );
      };
    });

    if (restore) {
      const same = (did) => lines.querySelector('[data-did="' + did + '"][data-dq="' + restore.dq + '"]');
      /* The cadence sync rewrites the plan half of every subscription
         line's identity, so the exact one can be gone while the flavour is
         right there. Same flavour, same button, before anything else:
         landing on another line's Decrease turned the next Enter into a
         removal of a different flavour. */
      const variant = String(restore.did).split(':')[0];
      const again =
        same(restore.did) ||
        lines.querySelector('[data-did^="' + variant + ':"][data-dq="' + restore.dq + '"]');
      /* The line can be gone: a remove, or a minus that reached zero. Land on
         the next control rather than dropping focus to the document. */
      const target =
        again || lines.querySelector('[data-dq]') || drawer.querySelector('.wg-drawer__x');
      if (target) target.focus({ preventScroll: true });
    }
  }

  /* Totals: our lines at the tier price, everything else at the cart's own
     price, so a mixed cart still adds up to what checkout will charge. */
  const saved = stats.subFull - stats.subPay;
  if (totalEl) totalEl.textContent = money(displayTotalCents(cart, stats));
  if (saveEl) {
    saveEl.hidden = !(saved > 0 && txt.saveLine);
    saveEl.textContent = saved > 0 ? String(txt.saveLine || '').replace('[amount]', money(saved)) : '';
  }

  /* Name the next rung. The ladder is invisible otherwise: nothing tells
     someone on 20% that a third bag is 25%, so the only way to find out is to
     try it. Shown only while a better rate is actually reachable, which under
     the 25% cap means up to two bags. */
  const rungEl = drawer.querySelector('[data-drawer-rung]');
  if (rungEl) {
    /* Subscription bags only. The tier is a subscription rate, so counting
       one-time bags told a full-price cart it was "on 20%" directly above a
       total that proved it was not. */
    const here = stats.subBags;
    const nowPct = here > 0 ? offerPct(here) : 0;
    const nextPct = offerPct(here + 1);
    const showRung = !!txt.nextRung && here > 0 && nextPct > nowPct;
    rungEl.hidden = !showRung;
    rungEl.textContent = showRung
      ? String(txt.nextRung).replace('[now]', nowPct).replace('[next]', nextPct)
      : '';
  }

  const threshold = CONFIG.freeShipQty || 0;
  const counted = FLAVORS.length ? stats.allBags : cart.item_count || 0;
  const progress = drawer.querySelector('.wg-dprog');
  if (progress) progress.hidden = !threshold;
  if (shipEl && threshold) {
    const need = threshold - counted;
    /* Three states: still short, threshold met, and top tier reached. The top
       message only fires when a higher tier actually exists, so a store with a
       single tier never claims a "highest" that is not there. */
    /* Free shipping counts every bag; the tier and its percent are
       subscription facts. Reading the tier off the bag total told a cart
       of three one-time bags it had reached the best rate. */
    const topTier = (CONFIG.topTierQty || 0) > 0 && stats.subBags >= CONFIG.topTierQty;
    shipEl.className = 'wg-dprog__txt' + (need <= 0 ? ' free' : '');
    if (need > 0) {
      shipEl.textContent = String(txt.shipNeed || '').replace(
        '[n]', need + ' more ' + (need > 1 ? 'bags' : 'bag')
      );
    } else {
      /* Crossing the threshold is the one genuinely good moment in a cart, so it
         gets a headline rather than another line of 12px grey. Three parts: the
         shout, what was actually unlocked, and the figure. Each is a setting,
         and emptying one drops that line without disturbing the others. */
      const fillTxt = (value) =>
        String(value || '')
          .replace('[amount]', money(saved))
          .replace('[pct]', String(subPct(stats.subBags)));
      const head = fillTxt(txt.wonHead);
      const sub = fillTxt(
        (topTier ? txt.shipTop : stats.subBags > 0 ? txt.shipWon : txt.shipFree) || txt.shipFree
      );
      const amount = saved > 0 ? fillTxt(txt.wonSaved) : '';
      shipEl.innerHTML =
        (head ? '<span class="wg-dwon__head">' + esc(head) + '</span>' : '') +
        (sub ? '<span class="wg-dwon__sub">' + esc(sub) + '</span>' : '') +
        (amount ? '<span class="wg-dwon__amt">' + esc(amount) + '</span>' : '');
      if (topTier) celebrate(drawer);
    }
  }
  if (fill && threshold) fill.style.width = Math.min(100, Math.round((counted / threshold) * 100)) + '%';

  /* The cost the subtotal leaves out. Saying it here is cheaper than having
     checkout say it, which is where carts are lost to it. */
  const shipNoteEl = drawer.querySelector('[data-drawer-shipnote]');
  if (shipNoteEl) {
    const wonShipping = threshold > 0 && counted >= threshold;
    const note = String((wonShipping ? txt.shipNoteFree : txt.shipNoteNeed) || '').replace(
      '[ship]',
      CONFIG.shipText || ''
    );
    shipNoteEl.hidden = !note || !items.length;
    shipNoteEl.textContent = note;
  }

  /* Delivery frequency: editing an existing choice, not a new decision, so it
     only appears once there is a subscription to change. */
  if (freqEl) {
    /* On the row rather than inside it, so it survives the innerHTML below
       and so the select itself is never replaced under a finger. */
    freqEl.classList.toggle('wg-dfreq--busy', intervalSlow);
    if (intervalSlow) freqEl.setAttribute('aria-busy', 'true');
    else freqEl.removeAttribute('aria-busy');
    const current = subIntervalMonths(cart);
    const ids = CONFIG.planIds || {};
    const intervals = planMonths();
    const canChange = stats.subBags > 0 && intervals.length > 1;
    freqEl.hidden = !canChange;
    const existing = freqEl.querySelector('[data-freq-select]');
    if (canChange && existing && document.activeElement === existing) {
      /* The shopper is on it. Rebuilding it here, from inside its own change
         event, took the element out from under the keyboard: the first
         arrow press changed the cadence and the second went nowhere. The
         options never change, so the value is all that needs setting. */
      existing.value = String(current);
    } else if (canChange) {
      const labels = { 1: txt.freq1, 2: txt.freq2, 3: txt.freq3 };
      freqEl.innerHTML =
        '<span>' + esc(txt.freqLabel || 'Delivery') + '</span>' +
        '<span class="wg-dfreq__sel">' +
        '<select data-freq-select aria-label="' + esc(txt.freqLabel || 'Delivery') + '">' +
        intervals
          .map(
            (n) =>
              '<option value="' + n + '"' + (current === n ? ' selected' : '') + '>' +
              esc(labels[n] || 'Every ' + n + ' months') +
              '</option>'
          )
          .join('') +
        '</select></span>';
      const select = freqEl.querySelector('[data-freq-select]');
      select.disabled = leaving;
      select.onchange = () => {
        if (!ids[select.value]) return;
        /* A deliberate choice stops the cart matching the cadence to the bag
           count for the rest of the session. Only a choice that differs from
           what the cart would have picked on its own is one: opening the
           picker and re-selecting the cadence already showing is not a
           decision to override anything, and pinning on it left the cadence
           stuck at three months on a cart that had been cut back to one bag.
           Picking the automatic value again hands control back. */
        const auto = monthsForBags(cartStats(cart).subBags);
        pinInterval(Number(select.value) !== auto ? Number(select.value) : false);
        /* Paint the choice now, commit behind it. This sends one request per
           subscription line, in sequence because each rotates the next one's
           key, so a three flavour cart used to sit on a disabled select for
           the better part of two seconds watching nothing happen. */
        localInterval(cart, Number(select.value));
        render(drawer);
        commitInterval(drawer, Number(select.value));
      };
    }
  }

  renderOffer(drawer, offerEl, txt);
  /* The panel is modal while open, so focus on the document is always a
     button this paint (or a disable) took away. Put it back inside. */
  settleFocus(drawer);
}

/* The one offer card. Its own function so render() can settle focus after
   it: the card is rebuilt on every paint, and a keyboard press on its
   button is a paint. */
function renderOffer(drawer, offerEl, txt) {
  if (!offerEl) return;
  const offer = computeOffer(cart);
  if (!offer) {
    offerEl.hidden = true;
    return;
  }
  let title = '';
  let text = '';
  let cta = '';
  if (offer.kind === 'sub') {
    title = txt.offerSubTitle;
    text = String(txt.offerSubText || '').replace('[pct]', offer.pct);
    cta = String(txt.offerSubCta || '').replace('[amount]', money(offer.save));
  } else {
    /* Below the free-shipping threshold, shipping is the bigger prize: the next
       bag saves the whole delivery charge, which beats a few points on the
       rate. Lead with that, and fall back to the rate copy above the threshold.
       Each shipping string is optional: leave one empty and that part of the
       card keeps its normal wording. */
    const ship = !!offer.winsShipping;
    const flavor = offer.flavor;
    const flavorName = flavor ? (flavor.emoji ? flavor.emoji + ' ' : '') + flavor.label : '';
    const fill = (value) => String(value || '')
      .replace('[pct]', offer.pct)
      .replace('[flavor]', flavorName)
      .replace('[ship]', CONFIG.shipText || '');
    title = (ship && txt.offerShipTitle) || txt.offerBagTitle;
    text = fill((ship && txt.offerShipText) || txt.offerBagText);
    cta = fill((ship && txt.offerShipCta) || (flavor ? txt.offerFlavorCta : txt.offerBagCta));
  }
  if (!cta) {
    offerEl.hidden = true;
    return;
  }
  offerEl.hidden = false;
  offerEl.className = 'wg-doffer' + (offerStreak % 2 === 1 ? ' wg-doffer--alt' : '');
  /* A refusal note survives repaints of the same cart (the checkout gate
     repaints on its way out, and used to wipe the one message that said
     why the bag is missing). Any cart change clears it. */
  const note = offerEl.querySelector('.wg-doffer__err');
  const keepNote = note && note.dataset.sig === cartSignature(cart) ? note : null;
  offerEl.innerHTML =
    '<div class="wg-doffer__title">' + esc(title) + '</div>' +
    '<div class="wg-doffer__text">' + esc(text) + '</div>' +
    '<button type="button" class="wg-doffer__cta" data-doffer>' + esc(cta) + '</button>';
  if (keepNote) offerEl.appendChild(keepNote);
  const button = offerEl.querySelector('[data-doffer]');
  /* A card rebuilt mid-switch must not come back with a live button. Marked
     rather than disabled so the keyboard user who pressed it keeps their
     place in the dialog; the flag refuses the second press. */
  if (offerBusy || leaving) button.setAttribute('aria-disabled', 'true');
  button.onclick = async () => {
    if (offerBusy || leaving) return;
    offerBusy = true;
    button.setAttribute('aria-disabled', 'true');
    try {
      await applyOffer(offer);
      offerStreak += 1;
      /* Before the repaint, or the card that follows a successful offer is
         painted with a dead button. */
      offerBusy = false;
      render(drawer);
      announce(
        drawer,
        String(txt.liveUpdate || '').replace(
          '[amount]',
          money(displayTotalCents(cart, cartStats(cart)))
        )
      );
    } catch (error) {
      /* A failure must be visible, never a silent re-render. The card is
         still busy until the message is up, so a checkout waiting on this
         offer leaves with the message on screen rather than before it. */
      await fetchCart();
      render(drawer);
      if (!offerEl.hidden && !offerEl.querySelector('.wg-doffer__err')) {
        const note = document.createElement('div');
        note.className = 'wg-doffer__err';
        note.dataset.sig = cartSignature(cart);
        note.textContent = (error && error.status === 429 ? txt.errorBusy : txt.error) || '';
        offerEl.appendChild(note);
      }
    } finally {
      offerBusy = false;
    }
  };
}

class WildgainzCartDrawer extends HTMLElement {
  connectedCallback() {
    if (this.__wgBound) return;
    this.__wgBound = true;

    this.querySelectorAll('[data-drawer-close]').forEach((el) => {
      el.addEventListener('click', () => this.close());
    });
    /* Dismiss on the scrim, named directly. This used to ask the opposite
       question, "did the click land outside the panel", which broke the
       moment the drawer started repainting on a press: a stepper's handler
       replaces the line list synchronously, so by the time the click bubbled
       up to here its target was an orphan node, closest() answered null, and
       a press on + read as a click on the background. Any test that asks
       where a node sits after other handlers have run carries that bug;
       asking which node was hit does not. */
    this.addEventListener('click', (event) => {
      if (event.target === this || event.target.closest('.wg-drawer__scrim')) this.close();
    });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const panel = this.querySelector('.wg-drawer__panel');
      if (!panel) return;
      const focusable = Array.prototype.filter.call(
        /* iframe included: an app can put one in the drawer, and a trap that
           cannot see it lets Tab walk out of the open dialog. */
        panel.querySelectorAll('button,a[href],select,input,iframe,[tabindex]'),
        (el) => !el.disabled && el.offsetParent !== null
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    const checkout = this.querySelector('[data-drawer-checkout]');
    if (checkout) {
      checkout.addEventListener('click', () => {
        /* The buy box assigns its own onclick to this button when it is on
           the page, and it comes back in through goToCheckout with its own
           tracking. Yield to it rather than report the same checkout twice.
           Off the product page nothing else claims the button, and this is
           the only begin_checkout there is. */
        if (typeof checkout.onclick === 'function') return;
        goToCheckout(this);
      });
    }

    /* The buy box's inline script runs before this deferred one, so it may
       have parked its flavor names on the window before we existed. */
    if (Array.isArray(window.WildgainzCartFlavors)) {
      window.WildgainzCart.setFlavors(window.WildgainzCartFlavors);
    }
    this.bindCartIcon();
    if (!this.classList.contains('open')) this.inert = true;
    /* Paint the panel now, while it is parked off screen and hidden.
       Rendering is the expensive part of opening, so doing it up front is
       what lets open() be a class flip and nothing else, leaving the slide a
       clear main thread. Every later cart change repaints it again. */
    fetchCart().then(() => render(this));
  }

  /* The theme editor replaces a section wholesale, and a drawer removed
     while open would otherwise leave the store pinned with nothing left to
     unpin it. */
  disconnectedCallback() {
    unlockPage();
  }

  /* The header icon opens the drawer instead of walking to the cart page.
     Guarded so the buy box, which used to bind this itself, cannot add a
     second listener and open two drawers. */
  bindCartIcon() {
    const icon = document.getElementById('cart-icon-bubble');
    if (!icon || icon.__wgCartBound) return;
    icon.__wgCartBound = true;
    icon.setAttribute('role', 'button');
    icon.setAttribute('aria-haspopup', 'dialog');
    icon.addEventListener('click', (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey) return;
      event.preventDefault();
      /* Straight to open, no round trip first. The panel is already painted
         and open() reconciles behind the slide, so the drawer starts moving
         on the tap instead of after the cart request. Fetching here would
         also defeat that reconcile: it refreshes the cart without repainting,
         so open() would compare the new cart against itself, find no
         difference, and leave a stale panel on screen. */
      this.open(icon);
    });
  }

  async open(triggeredBy) {
    const wasOpen = this.classList.contains('open');
    /* Already open: the opener is the element focus goes back to on close,
       and taking it again now would take the close button. */
    if (!wasOpen) this.__opener = triggeredBy || document.activeElement;
    /* Nothing is painted here on purpose. The panel is already current: it
       is rendered on load and after every cart change, while it is hidden
       and costs nothing. Writing the line list in the same task as this
       class flip used to delay the slide's first frame past 180ms on a slow
       phone and then teleport the panel 100px to catch up, which is what
       made opening look broken while closing looked fine. close() only
       removes a class, and now so does open(). */
    this.inert = false;
    this.classList.add('open');
    /* After the class flip, so the slide starts on the compositor before the
       page behind is relaid out. Safari ignores overflow:hidden on the body,
       which is why a drag that reached the end of the panel scrolled the
       store behind it; pinning the body at its current offset is the only
       lock it honours. */
    lockPage();
    /* preventScroll is defensive, not a measured fix. The close button sits
       in a panel still translated outside a clipped container, which invites
       the browser to scroll it into view and shunt the drawer sideways
       mid-slide. Chrome showed no shift either way; the option costs nothing
       and settles the question on engines that might. */
    const close = this.querySelector('.wg-drawer__x');
    if (close) close.focus({ preventScroll: true });
    /* Reconcile behind the slide, and repaint only if the server disagrees
       with what is on screen. Usually it does not, and a second innerHTML
       write landing mid-transition is the stutter again for no new pixels. */
    await fetchCart();
    if (this.classList.contains('open') && cartSignature(cart) !== paintedSignature) render(this);
    /* After the reconcile, so it reports the cart the shopper is actually
       looking at, and after the slide, so it costs the animation nothing.
       Only a real open counts: setActiveElement and renderContents both call
       this again while it is already open. */
    if (!wasOpen) track('view_cart', cart);
    /* A top tier reached while the panel was hidden owes the shopper its
       confetti. It waits out the slide: twenty-two animating divs is exactly
       the kind of work that costs the transition its frames. */
    if (celebrationPending) {
      celebrationPending = false;
      setTimeout(() => celebrate(this), 360);
    }
  }

  close() {
    unlockPage();
    this.classList.remove('open');
    /* Out of the tab order at once. The panel is visibility:hidden only
       once its slide has finished, and a Tab inside that window landed on
       a button sliding off the screen. */
    this.inert = true;
    if (this.__opener && this.__opener.isConnected) {
      try {
        /* preventScroll, or the browser scrolls the opener into view and
           undoes the position we just put back: the header cart icon is
           the usual opener and it can be well above where they were. */
        this.__opener.focus({ preventScroll: true });
      } catch (error) {
        /* the opener may have been re-rendered; focus is best effort */
      }
    }
    this.__opener = null;
  }

  /* ---------- the surface Dawn's scripts expect ---------- */
  getSectionsToRender() {
    return [{ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }];
  }

  setActiveElement(element) {
    this.__opener = element;
    /* Dawn calls this the moment an add starts, before the request goes out.
       Opening now puts the drawer up immediately and the new line lands in it
       when the response arrives, instead of a spinner for the whole wait. */
    if (!this.classList.contains('open')) this.open(element);
  }

  async renderContents(parsedState) {
    if (parsedState && parsedState.sections) updateBubble(parsedState.sections);
    /* An add from Dawn's product form: re-read, and let the cadence follow
       the new bag count the same as an add from anywhere else. */
    await noteAdd();
    if (!this.classList.contains('open')) await this.open(this.__opener);
  }
}

window.WildgainzCart = {
  /* The buy box hands over its nicer flavor names and emoji when it is on
     the page; the theme settings product list is the fallback everywhere. */
  setFlavors(list) {
    if (!Array.isArray(list) || !list.length) return;
    const byVariant = {};
    (CONFIG.flavors || []).forEach((flavor) => { byVariant[String(flavor.variantId)] = flavor; });
    list.forEach((flavor) => {
      if (!flavor || !flavor.variantId) return;
      byVariant[String(flavor.variantId)] = Object.assign({}, byVariant[String(flavor.variantId)] || {}, flavor);
    });
    FLAVORS = Object.keys(byVariant).map((key) => byVariant[key]);
    CONFIG.flavors = FLAVORS;
    indexFlavors();
  },
  /* The buy box passes the cart as it will be once its add lands, so the
     smart after-add choice is made against that and not the pre-add one. */
  computeOffer(data) { return computeOffer(data || cart); },
  stats() { return cartStats(cart); },
  /* The cadence the whole cart will want once `bags` more subscription bags
     are in it for `people`. The buy box asks before it adds, so a new bag
     goes straight onto the plan the cart is heading for and the sync has one
     less line to move. Same end state either way; this only skips the
     intermediate write. 0 when the cart does not match cadence to bags, so
     the caller keeps its own choice. */
  plannedMonths(bags, people) {
    if (!CONFIG.matchIntervalToBags) return 0;
    const pinned = intervalPinned();
    if (pinned) return pinned;
    /* The cadence the cart is on today, so new bags join the lines already
       there rather than starting fresh ones alongside them. Projecting the
       cadence the fuller cart will want reads better and behaves worse: the
       projection moves as the cart grows, so a shopper adding three times
       landed on three different plans and therefore three different lines,
       and the sync meant to bring them back together is exactly what stops
       running when the cart is being rate limited. Whatever the bag count
       ends up wanting, the sync moves one line per flavour, once. */
    const current = subIntervalMonths(serverCart || cart);
    if (current) return current;
    return monthsForBags(Number(bags) || 0, Number(people) || 1);
  },
  /* Lines just sent by the buy box, drawn as settling rows until noteAdd. */
  expect: expectLines,
  /* An add from outside the drawer landed (or failed: pass nothing to just
     clear the expected rows and re-read). */
  noteAdd: noteAdd,
  cart() { return cart; },
  el() {
    return document.querySelector('cart-drawer');
  },
  async open() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) await drawer.open(document.activeElement);
  },
  close() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.close();
  },
  refresh: fetchCart,
  money: money,
  /* For whoever owns the checkout button on a given page. */
  checkout(beforeLeave) { return goToCheckout(document.querySelector('cart-drawer[data-wg-cart]'), beforeLeave); },
  settle(maxMs) { return settle(document.querySelector('cart-drawer[data-wg-cart]'), maxMs, true); },
  pending() { return pendingWork(true); },
};

customElements.define('cart-drawer', WildgainzCartDrawer);


})();
